var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/alphaCss.ts
var alphaCss = (color, value) => {
  return `color-mix(in srgb, ${color} ${value * 100}%, transparent)`;
};

// src/components/ColorSchemeButton.tsx
import { DarkModeRounded, LightModeRounded } from "@mui/icons-material";
import { IconButton, useColorScheme } from "@mui/material";
import { RotationAnimation } from "@xylabs/react-animation";
import { FlexCol } from "@xylabs/react-flexbox";
import { useMemo, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var ColorSchemeButton = ({
  darkModeColor,
  lightModeColor,
  ...props
}) => {
  const {
    mode,
    setMode,
    systemMode
  } = useColorScheme();
  const resolvedMode = useMemo(() => (mode === "system" ? systemMode : mode) ?? "light", [mode, systemMode]);
  const [iconColor, setIconColor] = useState(() => resolvedMode === "dark" ? darkModeColor : lightModeColor);
  const [iconColorIsSet, setIconColorIsSet] = useState(false);
  const toggleMode = () => {
    if (mode === "light") {
      setMode("dark");
    } else if (mode === "dark") {
      setMode("system");
    } else {
      setMode("light");
    }
  };
  const handleHover = () => {
    setIconColor(() => {
      const darkModeIconColor = darkModeColor ?? "inherit";
      const lightModeIconColor = lightModeColor ?? "inherit";
      if (resolvedMode === "dark") {
        return iconColorIsSet ? darkModeIconColor : "info";
      } else {
        return iconColorIsSet ? lightModeIconColor : "warning";
      }
    });
    setIconColorIsSet(!iconColorIsSet);
  };
  return /* @__PURE__ */ jsx(FlexCol, { children: /* @__PURE__ */ jsx(RotationAnimation, { rotation: 20, children: /* @__PURE__ */ jsxs(IconButton, { color: iconColor, onClick: toggleMode, onMouseEnter: () => handleHover(), onMouseLeave: () => handleHover(), ...props, children: [
    resolvedMode === "dark" ? /* @__PURE__ */ jsx(DarkModeRounded, {}) : /* @__PURE__ */ jsx(LightModeRounded, {}),
    mode === "system" ? /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          position: "absolute",
          bottom: 2,
          fontSize: 8
        },
        children: "Auto"
      }
    ) : null
  ] }) }) });
};

// src/darkenCss.ts
var darkenCss = (color, value) => {
  return `color-mix(in srgb, ${color}, black ${value * 100}%)`;
};

// src/lightenCss.ts
var lightenCss = (color, value) => {
  return `color-mix(in srgb, ${color}, white ${value * 100}%)`;
};

// src/product-theme/dataism/theme.ts
import { alpha as alpha2, createTheme as createTheme4 } from "@mui/material";

// src/theme-fragments/components.tsx
import {
  alpha,
  createTheme,
  lighten
} from "@mui/material";
var componentFragment = (_theme) => createTheme({
  components: {
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alphaCss(theme.vars.palette.background.paper, 0.3),
          color: "inherit"
        }),
        standardError: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.error.main, 0.3), color: theme.vars.palette.error.light }),
        standardInfo: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.info.main, 0.3), color: theme.vars.palette.info.light }),
        standardSuccess: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.success.main, 0.3), color: theme.vars.palette.success.light }),
        standardWarning: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.warning.main, 0.3), color: theme.vars.palette.warning.light })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: "none",
          padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
          borderWidth: "2px",
          borderColor: "inherit"
        })
      },
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            "WebkitBackdropFilter": "blur(2px)",
            "backdropFilter": "blur(2px)",
            "border": "2px solid",
            "&:hover": { border: "2px solid" }
          }
        },
        {
          props: { variant: "text" },
          style: {
            "&:hover": {
              textDecoration: "underline 1.5px #66caf7",
              textUnderlineOffset: "5px",
              backgroundColor: "transparent"
            }
          }
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({ padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`, fontSize: "1rem" })
        }
      ]
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }
    },
    MuiCardContent: { styleOverrides: { root: ({ theme }) => ({ "&:last-child": { paddingBottom: theme.spacing(1) } }) } },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 400
        },
        colorError: { backgroundColor: alpha("#f6594e", 0.2), color: lighten("#f6594e", 0.2) },
        colorInfo: { backgroundColor: alpha("#72b4f4", 0.2), color: lighten("#72b4f4", 0.2) },
        colorPrimary: { backgroundColor: alpha("#5658F3", 0.2), color: lighten("#5658F3", 0.2) },
        colorSecondary: { backgroundColor: alpha("#66caf7", 0.2), color: lighten("#66caf7", 0.2) },
        colorSuccess: { backgroundColor: alpha("#7efc81", 0.2), color: lighten("#7efc81", 0.2) },
        colorWarning: { backgroundColor: alpha("#f7d866", 0.2), color: lighten("#f7d866", 0.2) }
      }
    },
    MuiContainer: { styleOverrides: { root: { maxWidth: "xl" } }, defaultProps: { maxWidth: "xl" } },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiDialog-paper": {
            backgroundImage: "none",
            paddingLeft: 2,
            paddingRight: 2
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            transitionDelay: "9999s",
            transitionProperty: "background-color, color"
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    },
    MuiLink: { styleOverrides: { root: { textDecoration: "none" } } }
  }
});

// src/theme-fragments/link.ts
var MuiLinkFragment = {
  MuiLink: {
    // default color prop is required because Mui passes 'primary' as the default color prop and overrides
    // color: 'inherit' in their own css
    defaultProps: {
      color: "inherit",
      underline: "none",
      fontWeight: 600
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "opacity": 0.45,
        "&:hover": { opacity: 0.8 },
        // Dark mode needs to be more opaque to be readable because the background is darker
        ...theme.applyStyles("dark", { opacity: 0.6 })
      })
    }
  }
};

// src/theme-fragments/shape.tsx
import { createTheme as createTheme2 } from "@mui/material";
var shapeFragment = (_theme) => createTheme2({ shape: { borderRadius: 4 } });

// src/theme-fragments/spacing.tsx
import { createTheme as createTheme3 } from "@mui/material";
var spacingFragment = (_theme) => createTheme3({ spacing: 8 });

// src/product-theme/ThemeExtensions/typography.ts
import "@mui/material/styles";
var display1 = { fontSize: "4rem" };
var display2 = {
  fontSize: "3.5rem",
  fontWeight: 400
};
var stylize1 = {
  fontSize: "1.25rem",
  fontWeight: 300,
  letterSpacing: "0.15em"
};
var stylize2 = {
  fontSize: "1rem",
  fontWeight: 700,
  letterSpacing: "0.1em"
};
var typographyFragmentDisplay1 = {
  fontSize: "4rem",
  fontWeight: 400
};
var typographyFragmentDisplay2 = {
  fontSize: "4rem",
  fontWeight: 400
};
var typographyFragmentStylize1 = {
  fontSize: "4rem",
  fontWeight: 400
};
var typographyFragmentStylize2 = {
  fontSize: "4rem",
  fontWeight: 400
};

// src/product-theme/dataism/customThemeColors.tsx
import { darken, lighten as lighten2 } from "@mui/material";
var personaColorsLightMode = {
  nova: {
    main: "#F6BF28",
    dark: "#D09E16",
    light: lighten2("#FADD8C", 0.4),
    contrastText: darken("#FADD8C", 0.8)
  },
  vex: {
    main: "#BFF07F",
    dark: "#8FCC3E",
    light: lighten2("#BFF07F", 0.4),
    contrastText: darken("#BFF07F", 0.8)
  },
  rook: {
    main: "#B0AED5",
    dark: "#615dac",
    light: lighten2("#B0AED5", 0.4),
    contrastText: darken("#B0AED5", 0.8)
  },
  lux: {
    main: "#FFDBF6",
    dark: "#FF5CD6",
    light: lighten2("#FFDBF6", 0.4),
    contrastText: darken("#FFDBF6", 0.8)
  },
  aegis: {
    main: "#B3C9F9",
    dark: "#7AA1F5",
    light: lighten2("#B3C9F9", 0.4),
    contrastText: darken("#B3C9F9", 0.8)
  },
  echo: {
    main: "#B0E4DA",
    dark: "#49C1A9",
    light: lighten2("#B0E4DA", 0.4),
    contrastText: darken("#B0E4DA", 0.8)
  },
  myca: {
    main: "#BF936D",
    dark: "#AA774B",
    light: lighten2("#BF936D", 0.4),
    contrastText: darken("#BF936D", 0.8)
  },
  cass: {
    main: "#b2cae4",
    dark: "#4078B5",
    light: lighten2("#b2cae4", 0.4),
    contrastText: darken("#b2cae4", 0.8)
  }
};
var personaColorsDarkMode = {
  nova: {
    main: "#FADD8C",
    contrastText: darken("#FADD8C", 0.9),
    light: lighten2("#FADD8C", 0.4),
    dark: darken("#FADD8C", 0.5)
  },
  vex: {
    main: "#BFF07F",
    contrastText: darken("#BFF07F", 0.9),
    light: lighten2("#BFF07F", 0.4),
    dark: darken("#BFF07F", 0.5)
  },
  rook: {
    main: "#8B88C2",
    contrastText: darken("#8B88C2", 0.9),
    light: lighten2("#8B88C2", 0.4),
    dark: darken("#8B88C2", 0.5)
  },
  lux: {
    main: "#EB97D6",
    contrastText: darken("#EB97D6", 0.9),
    light: lighten2("#EB97D6", 0.4),
    dark: darken("#EB97D6", 0.5)
  },
  aegis: {
    main: "#7aa1f5",
    contrastText: darken("#7aa1f5", 0.9),
    light: lighten2("#7aa1f5", 0.4),
    dark: darken("#7aa1f5", 0.5)
  },
  echo: {
    main: "#B0E4DA",
    contrastText: darken("#B0E4DA", 0.9),
    light: lighten2("#B0E4DA", 0.4),
    dark: darken("#B0E4DA", 0.5)
  },
  myca: {
    main: "#BF936D",
    contrastText: darken("#BF936D", 0.9),
    light: lighten2("#BF936D", 0.4),
    dark: darken("#BF936D", 0.5)
  },
  cass: {
    main: "#b2cae4",
    contrastText: darken("#b2cae4", 0.9),
    light: lighten2("#b2cae4", 0.4),
    dark: darken("#b2cae4", 0.5)
  }
};

// src/product-theme/dataism/darkThemePalette.tsx
var darkThemePalette = {
  background: {
    paper: "#1E1E1E",
    gradient: "linear-gradient(45deg, #fff, #000)"
  },
  info: { main: "#72b4f4" },
  primary: { main: "#fff" },
  secondary: {
    main: "#66caf7",
    contrastText: "#020223"
  },
  success: {
    main: "#7efc81",
    contrastText: "#011e01"
  },
  warning: { main: "#f7d866" },
  error: { main: "#f6594e" },
  text: {
    disabled: "#a5acdb",
    primary: "#E3E4EB",
    secondary: "#e3e4eba3"
  },
  personas: personaColorsDarkMode
};

// src/product-theme/dataism/lightThemePalette.tsx
var lightThemePalette = {
  background: {
    paper: "#FAFAFA",
    gradient: "linear-gradient(45deg, #000, #fff)"
  },
  info: { main: "#72b4f4" },
  primary: { main: "#000" },
  secondary: { main: "#186ecc" },
  success: { main: "#48BA4B", contrastText: "#fff" },
  text: {
    disabled: "#D3D0CB",
    primary: "#1E2019",
    secondary: "#393E41"
  },
  personas: personaColorsLightMode
};

// src/product-theme/dataism/theme.ts
var DataismThemeOptions = {
  colorSchemes: {
    dark: { palette: darkThemePalette },
    light: { palette: lightThemePalette }
  },
  cssVariables: { colorSchemeSelector: "class" },
  breakpoints: {
    values: {
      lg: 1350,
      md: 900,
      sm: 600,
      xl: 1536,
      xs: 0
    }
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha2(theme.palette.background.paper, 0.3),
          color: "inherit"
        }),
        standardError: ({ theme }) => ({ backgroundColor: alpha2(theme.palette.error.main, 0.3), color: theme.palette.error.light }),
        standardInfo: ({ theme }) => ({ backgroundColor: alpha2(theme.palette.info.main, 0.3), color: theme.palette.info.light }),
        standardSuccess: ({ theme }) => ({ backgroundColor: alpha2(theme.palette.success.main, 0.3), color: theme.palette.success.light }),
        standardWarning: ({ theme }) => ({ backgroundColor: alpha2(theme.palette.warning.main, 0.3), color: theme.palette.warning.light })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: "none",
          padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
          borderWidth: "2px",
          borderColor: "inherit"
        })
      },
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            "WebkitBackdropFilter": "blur(2px)",
            "backdropFilter": "blur(2px)",
            "border": "2px solid",
            "&:hover": { border: "2px solid" }
          }
        },
        {
          props: { variant: "text" },
          style: {
            "&:hover": {
              textDecoration: "underline 1.5px #66caf7",
              textUnderlineOffset: "5px",
              backgroundColor: "transparent"
            }
          }
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({ padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`, fontSize: "1rem" })
        }
      ]
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }
    },
    MuiCardContent: { styleOverrides: { root: ({ theme }) => ({ "&:last-child": { paddingBottom: theme.spacing(1) } }) } },
    MuiContainer: { styleOverrides: { root: { maxWidth: "xl" } }, defaultProps: { maxWidth: "xl" } },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiDialog-paper": {
            backgroundImage: "none",
            paddingLeft: 2,
            paddingRight: 2
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            transitionDelay: "9999s",
            transitionProperty: "background-color, color"
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    },
    ...MuiLinkFragment
  },
  ...spacingFragment,
  ...shapeFragment,
  typography: {
    body1: { fontSize: "1.1rem" },
    button: {
      fontSize: "1rem",
      textTransform: "inherit"
    },
    fontFamily: '"Fustat", sans-serif',
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    h1: { fontSize: "3.2rem" },
    h2: { fontSize: "2.7rem" },
    h3: { fontSize: "2.24rem" },
    h4: { fontSize: "2rem" },
    h5: { fontSize: "1.5rem" },
    h6: { fontSize: "1.25rem" },
    subtitle1: {
      opacity: "50%",
      textTransform: "uppercase"
    },
    subtitle2: { opacity: "50%" }
  }
};
var DataismTheme = (_theme) => createTheme4(DataismThemeOptions);

// src/product-theme/xl1/theme.ts
import { alpha as alpha3, createTheme as createTheme5 } from "@mui/material";

// src/product-theme/xl1/darkThemeOptions.tsx
var darkThemeOptions = {
  palette: {
    background: { default: "#020223", paper: "#020223" },
    error: { main: "#f6594e" },
    success: {
      main: "#7efc81",
      contrastText: "#011e01"
    },
    primary: { main: "#5658F3", light: "#7c72ff" },
    secondary: {
      main: "#66caf7",
      contrastText: "#020223"
    },
    neutral: {
      main: "#fff",
      contrastText: "#111",
      dark: "#ccc"
    },
    warning: { main: "#f7d866" },
    text: {
      disabled: "#a5acdb",
      primary: "#E3E4EB",
      secondary: "#e3e4eba3"
    }
  }
};

// src/product-theme/xl1/lightThemeOptions.tsx
var lightThemeOptions = {
  palette: {
    background: {
      default: "#F4F4F4",
      paper: "#FFFFFF"
    },
    error: {
      dark: "#AA0E0E",
      light: "#F15555",
      main: "#D81111"
    },
    neutral: {
      main: "#000",
      contrastText: "#fff",
      dark: "#333"
    },
    primary: { main: "#463dc6", light: "#b2adfb" },
    secondary: { main: "#186ecc" },
    success: {
      dark: "#347f21",
      light: "#94dd80",
      main: "#62bc50"
    },
    text: {
      disabled: "#49454D",
      primary: "#0d0d47",
      secondary: "#49454D"
    },
    warning: { main: "#ffc805" }
  }
};

// src/product-theme/xl1/theme.ts
var Xl1ThemeOptions = {
  colorSchemes: {
    dark: darkThemeOptions,
    light: lightThemeOptions
  },
  cssVariables: { colorSchemeSelector: "class" },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&.Mui-expanded": { "&$expanded": { margin: "4px 0" } },
          "boxShadow": "none"
        }
      }
    },
    MuiAccordionSummary: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          WebkitBackdropFilter: "blur(20px)",
          backdropFilter: "blur(20px)",
          backgroundColor: alpha3(theme.palette.background.paper, 0.2),
          boxShadow: "none",
          color: "#16163D",
          ...theme.applyStyles("dark", { color: "#CBC4CE" })
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // This is to get rid of the extra padding at the end of Card Content that misaligns padding when there's only 1 item
          "&:last-child": { paddingBottom: "16px" }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        content: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }
      }
    },
    MuiContainer: { styleOverrides: { root: { maxWidth: "xl" } } },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiTypography-root, & .MuiButton-root": { color: "#FFFFFF" },
          "paperProps": { backgroundColor: "#19193F" },
          ...theme.applyStyles("dark", { color: theme.palette.secondary.main })
        })
      }
    },
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-error": { border: `${theme.palette.error.main} 1px solid` },
          "border": "transparent 1px solid",
          "borderRadius": theme.shape.borderRadius
        })
      }
    },
    ...MuiLinkFragment,
    MuiTableCell: {
      styleOverrides: {
        body: {
          fontFamily: "monospace",
          whiteSpace: "nowrap"
        },
        head: {
          fontWeight: 700,
          whiteSpace: "nowrap"
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          fontSize: "0.75rem",
          maxWidth: 250,
          border: `1px solid ${theme.palette.background.default}`,
          padding: theme.spacing(1),
          boxShadow: "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px"
        })
      }
    }
  },
  ...spacingFragment,
  ...shapeFragment,
  typography: {
    allVariants: { fontFamily: ["Manrope", "san-serif"].join(",") },
    body1: { fontSize: "16px" },
    button: {
      fontSize: "1rem",
      textTransform: "capitalize"
    },
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 600,
    fontWeightRegular: 500,
    h1: { fontSize: "3.2rem" },
    h2: { fontSize: "2.7rem" },
    h3: { fontSize: "2.24rem" },
    h4: { fontSize: "2rem" },
    h5: { fontSize: "1.5rem" },
    h6: { fontSize: "1.2rem" },
    subtitle1: {
      fontWeight: 300,
      opacity: "50%",
      textTransform: "uppercase"
    },
    subtitle2: { opacity: "50%" }
  }
};
var Xl1Theme = (_theme) => createTheme5(Xl1ThemeOptions);

// src/product-theme/xylabs/theme.ts
import { createTheme as createTheme6 } from "@mui/material";

// src/product-theme/xylabs/darkThemeOptions.tsx
var darkThemeOptions2 = {
  palette: {
    background: {
      default: "#0b0f30",
      paper: "#101742",
      gradient: "linear-gradient(45deg, #384AFD, #0b0f30)"
    },
    primary: {
      dark: "#010965",
      light: "#EBECFF",
      main: "#384AFD"
    },
    secondary: {
      dark: "#AD8E13",
      light: "#F9D549",
      main: "#FFC91D"
    },
    text: {
      disabled: "#a5acdb",
      primary: "#f7f8fc",
      secondary: "#daddf2"
    }
  }
};

// src/product-theme/xylabs/lightThemeOptions.tsx
var lightThemeOptions2 = {
  palette: {
    background: {
      default: "#fafafa",
      gradient: "linear-gradient(45deg, #384AFD, #fafafa)"
    },
    primary: {
      dark: "#010965",
      light: "#EBECFF",
      main: "#384AFD"
    },
    secondary: {
      dark: "#AD8E13",
      light: "#F9D549",
      main: "#FFC91D"
    },
    error: { main: "#CC183C", contrastText: "#FDEDF0" },
    info: { main: "#186ecc", contrastText: "#DAEAFB" },
    success: { main: "#7efc81", contrastText: "#142B12" },
    warning: { main: "#F4813E", contrastText: "#271002" },
    text: { primary: "#1F2025" }
  }
};

// src/product-theme/xylabs/theme.ts
var XyLabsThemeOptions = {
  colorSchemes: {
    light: lightThemeOptions2,
    dark: darkThemeOptions2
  },
  cssVariables: { colorSchemeSelector: "class" },
  ...componentFragment,
  ...spacingFragment,
  ...shapeFragment,
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          paddingBottom: 0.5,
          paddingTop: 0.5
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        outlined: { backgroundColor: "inherit" },
        root: { overflow: "hidden" }
      }
    },
    ...MuiLinkFragment,
    MuiStepper: { styleOverrides: { root: { padding: "0px" } } }
  },
  typography: {
    fontFamily: '"Outfit", sans-serif',
    body1: { fontSize: "1.1rem" },
    button: {
      fontSize: "1rem",
      fontWeight: 500,
      textTransform: "capitalize"
    },
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 400,
    fontWeightRegular: 400,
    h1: { fontSize: "4rem" },
    h2: { fontSize: "2.7rem" },
    h3: { fontSize: "2.24rem" },
    h4: { fontSize: "2rem" },
    h5: { fontSize: "1.5rem" },
    h6: { fontSize: "1.25rem" },
    subtitle1: {
      opacity: "50%",
      textTransform: "uppercase"
    },
    subtitle2: { opacity: "50%" }
  }
};
var XyLabsTheme = (_theme) => createTheme6(XyLabsThemeOptions);

// src/product-theme/xyo/breakpoints.ts
var XyoThemeBreakpoints = {
  breakpoints: {
    values: {
      lg: 1350,
      md: 900,
      sm: 600,
      xl: 1536,
      xs: 0
    }
  }
};

// src/product-theme/xyo/components.ts
import { alpha as alpha4 } from "@mui/material";
var XyoThemeComponents = {
  components: {
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alphaCss(theme.vars.palette.background.paper, 0.3),
          color: "inherit"
        }),
        standardError: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.error.main, 0.3), color: theme.vars.palette.error.light }),
        standardInfo: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.info.main, 0.3), color: theme.vars.palette.info.light }),
        standardSuccess: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.success.main, 0.3), color: theme.vars.palette.success.light }),
        standardWarning: ({ theme }) => ({ backgroundColor: alphaCss(theme.vars.palette.warning.main, 0.3), color: theme.vars.palette.warning.light })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: "none",
          padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
          borderWidth: "2px",
          borderColor: "inherit"
        })
      },
      variants: [
        {
          props: { variant: "outlined" },
          style: ({ theme }) => ({
            "WebkitBackdropFilter": "blur(2px)",
            "backdropFilter": "blur(2px)",
            "border": `2px solid ${alpha4("#fff", 0.4)}`,
            "color": "#fff",
            "&:hover": {
              backgroundColor: "#ffffff",
              border: `2px solid ${alpha4("#fff", 0)}`,
              color: darkenCss(theme.vars.palette.primary.dark, 0.7)
            }
          })
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({ padding: `${theme.spacing(0.5)} ${theme.spacing(1)}` })
        }
      ]
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }
    },
    MuiContainer: { styleOverrides: { root: { maxWidth: "xl" } } },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            transitionDelay: "9999s",
            transitionProperty: "background-color, color"
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    },
    MuiLink: {
      // default color prop is required because Mui passes 'primary' as the default color prop and overrides
      // color: 'inherit' in their own css
      defaultProps: {
        color: "inherit",
        underline: "none",
        fontWeight: 600
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "inherit",
            WebkitTextFillColor: "inherit",
            caretColor: "inherit"
          }
        }
      }
    }
  }
};

// src/product-theme/xyo/darkThemeOptions.ts
var XyoThemeDarkThemeOptions = {
  palette: {
    background: {
      default: "#000",
      paper: "#111111"
    },
    neutral: {
      main: "#fff",
      contrastText: "#000"
    },
    info: { main: "#72b4f4" },
    primary: { main: "#5658F3" },
    secondary: {
      main: "#37CEFF",
      contrastText: "#020223"
    },
    success: {
      main: "#7efc81",
      contrastText: "#011e01"
    },
    warning: { main: "#f7d866" },
    error: { main: "#f6594e" },
    text: {
      disabled: "#a5acdb",
      primary: "#E3E4EB",
      secondary: "#e3e4eba3"
    }
  }
};

// src/product-theme/xyo/lightThemeOptions.ts
import { alpha as alpha5 } from "@mui/material";
var XyoThemeLightThemeOptions = {
  palette: {
    background: {
      paper: "#FAFAFA",
      gradient: "linear-gradient(45deg, #72b4f4, #fff)"
    },
    neutral: {
      main: "#000",
      contrastText: "#fff",
      dark: "#333"
    },
    info: { main: "#72b4f4" },
    primary: { main: "#463dc6" },
    secondary: { main: "#186ecc" },
    success: { main: "#48BA4B" },
    text: {
      primary: "#060312",
      secondary: alpha5("#060312", 0.85)
    }
  }
};

// src/product-theme/xyo/theme.ts
import { createTheme as createTheme7 } from "@mui/material";

// src/product-theme/xyo/typography.ts
var XyoThemeTypography = {
  typography: {
    fontFamily: '"Lexend Deca", sans-serif',
    body1: { fontSize: "1.1rem" },
    button: {
      fontSize: "1rem",
      fontWeight: 500,
      textTransform: "capitalize"
    },
    fontWeightBold: 600,
    fontWeightLight: 200,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    h1: {
      fontSize: "3.2rem",
      fontWeight: 400
    },
    h2: {
      fontSize: "2.7rem",
      fontWeight: 400
    },
    h3: {
      fontSize: "2.24rem",
      fontWeight: 400
    },
    h4: {
      fontSize: "2rem",
      fontWeight: 400
    },
    h5: {
      fontSize: "1.5rem",
      fontWeight: 400
    },
    h6: {
      fontSize: "1.25rem",
      fontWeight: 400
    },
    subtitle1: {
      opacity: "50%",
      textTransform: "uppercase"
    },
    subtitle2: { opacity: "50%" }
  }
};

// src/product-theme/xyo/theme.ts
var XyoThemeOptions = {
  colorSchemes: {
    dark: { ...XyoThemeDarkThemeOptions },
    light: { ...XyoThemeLightThemeOptions }
  },
  cssVariables: { colorSchemeSelector: "class" },
  ...XyoThemeBreakpoints,
  ...XyoThemeComponents,
  shape: { borderRadius: 8 },
  spacing: 12,
  ...XyoThemeTypography
};
var XyoTheme = (_theme) => createTheme7(XyoThemeOptions);

// src/product-theme/xyos/theme.ts
import { createTheme as createTheme8 } from "@mui/material";

// src/product-theme/xyos/darkThemeOptions.tsx
var darkThemeOptions3 = {
  palette: {
    background: { default: "#020223", paper: "#020223" },
    error: { main: "#f6594e" },
    neutral: {
      main: "#fff",
      contrastText: "#111",
      dark: "#ccc"
    },
    primary: { main: "#5658F3", light: "#7c72ff" },
    secondary: {
      main: "#66caf7",
      contrastText: "#020223"
    },
    success: {
      main: "#7efc81",
      contrastText: "#011e01"
    },
    text: {
      disabled: "#a5acdb",
      primary: "#E3E4EB",
      secondary: "#e3e4eba3"
    },
    warning: { main: "#f7d866" }
  }
};

// src/product-theme/xyos/lightThemeOptions.tsx
var lightThemeOptions3 = {
  palette: {
    background: {
      default: "#F4F4F4",
      paper: "#FFFFFF"
    },
    error: {
      dark: "#AA0E0E",
      light: "#F15555",
      main: "#D81111"
    },
    neutral: {
      main: "#000",
      contrastText: "#fff",
      dark: "#333"
    },
    primary: { main: "#463dc6", light: "#b2adfb" },
    secondary: { main: "#186ecc" },
    success: {
      dark: "#347f21",
      light: "#94dd80",
      main: "#62bc50"
    },
    text: {
      disabled: "#49454D",
      primary: "#0d0d47",
      secondary: "#49454D"
    },
    warning: { main: "#ffc805" }
  }
};

// src/product-theme/xyos/theme.ts
var XyOSThemeOptions = {
  colorSchemes: {
    dark: darkThemeOptions3,
    light: lightThemeOptions3
  },
  cssVariables: { colorSchemeSelector: "class" },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&.Mui-expanded": { "&$expanded": { margin: "4px 0" } },
          "boxShadow": "none"
        }
      }
    },
    MuiAccordionSummary: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          WebkitBackdropFilter: "blur(20px)",
          backdropFilter: "blur(20px)",
          boxShadow: "none",
          color: "#16163D",
          ...theme.applyStyles("dark", { color: "#CBC4CE" })
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // This is to get rid of the extra padding at the end of Card Content that misaligns padding when there's only 1 item
          "&:last-child": { paddingBottom: "16px" }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        content: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }
      }
    },
    MuiContainer: { styleOverrides: { root: { maxWidth: "xl" } } },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiTypography-root, & .MuiButton-root": { color: "#FFFFFF" },
          "paperProps": { backgroundColor: "#19193F" },
          ...theme.applyStyles("dark", { color: theme.palette.secondary.main })
        })
      }
    },
    MuiFilledInput: {
      defaultProps: { disableUnderline: true },
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-error": { border: `${theme.palette.error.main} 1px solid` },
          "border": "transparent 1px solid",
          "borderRadius": theme.shape.borderRadius
        })
      }
    },
    ...MuiLinkFragment,
    MuiTableCell: {
      styleOverrides: {
        body: {
          fontFamily: "monospace",
          whiteSpace: "nowrap"
        },
        head: {
          fontWeight: 700,
          whiteSpace: "nowrap"
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          color: "inherit",
          fontSize: "0.75rem",
          maxWidth: 250,
          border: `1px solid ${theme.palette.background.default}`,
          padding: theme.spacing(1),
          boxShadow: "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px"
        })
      }
    }
  },
  spacing: 12,
  ...shapeFragment,
  typography: {
    allVariants: { fontFamily: ["Manrope", "san-serif"].join(",") },
    body1: { fontSize: "16px" },
    button: {
      fontSize: "1rem",
      textTransform: "capitalize"
    },
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 600,
    fontWeightRegular: 500,
    h1: { fontSize: "3.2rem" },
    h2: { fontSize: "2.7rem" },
    h3: { fontSize: "2.24rem" },
    h4: { fontSize: "2rem" },
    h5: { fontSize: "1.5rem" },
    h6: { fontSize: "1.2rem" },
    subtitle1: {
      fontWeight: 300,
      opacity: "50%",
      textTransform: "uppercase"
    },
    subtitle2: { opacity: "50%" }
  }
};
var XyosTheme = () => createTheme8(XyOSThemeOptions);

// src/showcase/dashboard/StandardComponents/AppChrome/AppChrome.tsx
import { MenuRounded } from "@mui/icons-material";
import {
  AppBar as MuiAppBar,
  Box,
  CssBaseline,
  IconButton as IconButton3,
  styled as styled4,
  Toolbar as Toolbar2,
  Typography as Typography2,
  useTheme as useTheme4
} from "@mui/material";
import { FlexRow as FlexRow3 } from "@xylabs/react-flexbox";
import { useState as useState3 } from "react";
import {
  Navigate,
  Route,
  Routes
} from "react-router-dom";

// src/showcase/dashboard/Drawer/LogoutListItem.tsx
import { LogoutRounded } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from "@mui/material";
import { useState as useState2 } from "react";

// src/showcase/dashboard/Drawer/StyledListItemButton.tsx
import { ListItemButton, styled } from "@mui/material";
import { FlexRow } from "@xylabs/react-flexbox";
var StyledMenuIconWrapSpan = styled("span", { name: "StyledMenuIconWrapSpan" })(() => ({
  alignItems: "center",
  display: "inline-flex",
  flexDirection: "column"
}));
var StyledFlexRow = styled(FlexRow, { name: "StyledFlexRow" })(({ theme }) => ({
  "&:hover": { backgroundColor: alphaCss(theme.vars.palette.text.primary, 0.05) },
  "borderRadius": "10px",
  "paddingLeft": theme.spacing(0.5),
  "padding": theme.spacing(0.5),
  "flexGrow": 1
}));
var StyledListItemButton = styled(ListItemButton, { name: "StyledListItemButton", shouldForwardProp: (prop) => prop !== "active" })(({ theme, active }) => {
  const activeOrHoverColor = theme.vars.palette.primary.main;
  return {
    "position": "relative",
    "padding": "8px 16px",
    "borderRadius": "12px",
    "transition": "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "transparent",
      borderRadius: "12px"
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "6px",
      backgroundColor: active ? activeOrHoverColor : "transparent",
      borderRadius: "0 4px 4px 0",
      transition: "background-color 0.3s ease"
    }
  };
});

// src/showcase/dashboard/Drawer/LogoutListItem.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var LogoutListItem = (props) => {
  const [open, setOpen] = useState2(false);
  const theme = useTheme();
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2(
      ListItem,
      {
        disablePadding: true,
        sx: { paddingBottom: 1 },
        ...props,
        children: /* @__PURE__ */ jsx2(
          StyledListItemButton,
          {
            onClick: handleClickOpen,
            disableRipple: true,
            disableTouchRipple: true,
            active: false,
            children: /* @__PURE__ */ jsxs2(StyledFlexRow, { children: [
              /* @__PURE__ */ jsx2(
                ListItemIcon,
                {
                  sx: { color: alphaCss(theme.vars.palette.text.primary, 0.5), justifyContent: "center" },
                  children: /* @__PURE__ */ jsx2(LogoutRounded, {})
                }
              ),
              /* @__PURE__ */ jsx2(
                ListItemText,
                {
                  sx: { color: alphaCss(theme.vars.palette.text.primary, 0.5) },
                  primary: "Logout"
                }
              )
            ] })
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs2(
      Dialog,
      {
        open,
        onClose: handleClose,
        "aria-labelledby": "logout-dialog-title",
        children: [
          /* @__PURE__ */ jsx2(DialogTitle, { id: "logout-dialog-title", children: "Confirm Logout" }),
          /* @__PURE__ */ jsx2(DialogContent, { children: /* @__PURE__ */ jsx2(DialogContentText, { children: "Are you sure you want to log out?" }) }),
          /* @__PURE__ */ jsxs2(DialogActions, { children: [
            /* @__PURE__ */ jsx2(Button, { onClick: handleClose, color: "primary", children: "Cancel" }),
            /* @__PURE__ */ jsx2(Button, { color: "primary", autoFocus: true, children: "Logout" })
          ] })
        ]
      }
    )
  ] });
};

// src/showcase/dashboard/Drawer/MenuList.tsx
import {
  List,
  ListItem as ListItem2,
  ListItemIcon as ListItemIcon2,
  ListItemText as ListItemText2,
  useTheme as useTheme2
} from "@mui/material";
import { isDefined } from "@xylabs/typeof";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

// src/showcase/dashboard/StyledListItemButton.tsx
import { ListItemButton as ListItemButton2, styled as styled2 } from "@mui/material";
import { FlexRow as FlexRow2 } from "@xylabs/react-flexbox";
var StyledMenuIconWrapSpan2 = styled2("span", { name: "StyledMenuIconWrapSpan" })(() => ({
  alignItems: "center",
  display: "inline-flex",
  flexDirection: "column"
}));
var StyledFlexRow2 = styled2(FlexRow2, { name: "StyledFlexRow" })(({ theme }) => ({
  "&:hover": { backgroundColor: alphaCss(theme.vars.palette.text.primary, 0.05) },
  "borderRadius": "10px",
  "paddingLeft": theme.spacing(0.5),
  "padding": theme.spacing(0.5),
  "flexGrow": 1
}));
var StyledListItemButton2 = styled2(ListItemButton2, { name: "StyledListItemButton", shouldForwardProp: (prop) => prop !== "active" })(({ theme, active }) => {
  const activeOrHoverColor = theme.vars.palette.primary.main;
  return {
    "position": "relative",
    "padding": "8px 16px",
    "borderRadius": "12px",
    "transition": "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "transparent",
      borderRadius: "12px"
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "6px",
      backgroundColor: active ? activeOrHoverColor : "transparent",
      borderRadius: "0 4px 4px 0",
      transition: "background-color 0.3s ease"
    }
  };
});

// src/showcase/dashboard/Drawer/MenuList.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var BaseMenuList = (props) => {
  const {
    menuItems,
    sx,
    ...listProps
  } = props;
  const theme = useTheme2();
  const activeColor = theme.vars.palette.primary.light;
  const { pathname } = useLocation();
  const pathMatch = useCallback((path, matchType) => {
    if (matchType === "exact") {
      return pathname === path || pathname === `${path}/`;
    }
    return pathname.startsWith(path);
  }, [pathname]);
  return /* @__PURE__ */ jsx3(
    List,
    {
      sx: {
        p: 0,
        overflow: "auto",
        ...sx
      },
      ...listProps,
      children: menuItems?.map(({
        svgIcon: icon,
        primaryText,
        path,
        matchType
      }) => /* @__PURE__ */ jsx3(
        ListItem2,
        {
          disablePadding: true,
          sx: { paddingBottom: 1 },
          children: /* @__PURE__ */ jsx3(
            StyledListItemButton2,
            {
              active: pathMatch(path, matchType),
              disableRipple: true,
              disableTouchRipple: true,
              children: /* @__PURE__ */ jsxs3(StyledFlexRow2, { children: [
                /* @__PURE__ */ jsx3(
                  ListItemIcon2,
                  {
                    sx: { color: pathMatch(path, matchType) ? activeColor : alphaCss(theme.vars.palette.text.primary, 0.5), justifyContent: "center" },
                    children: /* @__PURE__ */ jsx3(
                      StyledMenuIconWrapSpan2,
                      {
                        ref: (ref) => {
                          if (isDefined(ref)) {
                            ref.innerHTML = icon ?? "";
                          }
                        }
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx3(
                  ListItemText2,
                  {
                    slotProps: {
                      primary: {
                        fontWeight: pathMatch(path, matchType) ? "bold" : "inherit",
                        color: pathMatch(path, matchType) ? "inherit" : alphaCss(theme.vars.palette.text.primary, 0.5)
                      }
                    },
                    primary: primaryText
                  }
                )
              ] })
            }
          )
        },
        primaryText
      ))
    }
  );
};

// src/showcase/dashboard/Drawer/MiniDrawer.tsx
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import {
  Divider,
  IconButton as IconButton2,
  List as List2
} from "@mui/material";
import { FlexGrowCol } from "@xylabs/react-flexbox";
import { useLocation as useLocation2, useNavigate } from "react-router-dom";

// src/showcase/dashboard/Drawer/BottomMenuArea.tsx
import {
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { FlexCol as FlexCol2 } from "@xylabs/react-flexbox";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var BottomMenuArea = (props) => {
  return /* @__PURE__ */ jsxs4(FlexCol2, { alignContent: "center", flexWrap: "wrap", ...props, children: [
    /* @__PURE__ */ jsx4(Toolbar, { sx: { paddingX: 1.5 }, children: /* @__PURE__ */ jsx4(Tooltip, { title: "Toggle Light/Dark Mode", children: /* @__PURE__ */ jsx4("span", { children: /* @__PURE__ */ jsx4(ColorSchemeButton, {}) }) }) }),
    /* @__PURE__ */ jsxs4(Typography, { variant: "body2", children: [
      "\xA9 \xA0",
      (/* @__PURE__ */ new Date()).getFullYear()
    ] })
  ] });
};

// src/showcase/dashboard/Drawer/MenuListItem.tsx
import { LogoutRounded as LogoutRounded2 } from "@mui/icons-material";
import {
  ListItem as ListItem3,
  ListItemIcon as ListItemIcon3,
  ListItemText as ListItemText3,
  useTheme as useTheme3
} from "@mui/material";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var defaultIcon = /* @__PURE__ */ jsx5(LogoutRounded2, {});
var MenuListItem = ({
  icon = defaultIcon,
  text,
  onItemClick,
  mode = "full-width",
  active,
  ...props
}) => {
  const theme = useTheme3();
  return /* @__PURE__ */ jsx5(
    ListItem3,
    {
      disablePadding: true,
      sx: { paddingBottom: 1 },
      ...props,
      children: /* @__PURE__ */ jsx5(
        StyledListItemButton2,
        {
          onClick: onItemClick,
          disableRipple: true,
          disableTouchRipple: true,
          active,
          children: /* @__PURE__ */ jsxs5(StyledFlexRow2, { children: [
            /* @__PURE__ */ jsx5(
              ListItemIcon3,
              {
                sx: { color: alphaCss(theme.vars.palette.text.primary, 0.5), justifyContent: "center" },
                children: icon
              }
            ),
            mode === "full-width" && /* @__PURE__ */ jsx5(
              ListItemText3,
              {
                sx: { color: alphaCss(theme.vars.palette.text.primary, 0.5) },
                primary: text
              }
            )
          ] })
        }
      )
    }
  );
};

// src/showcase/dashboard/Drawer/Styling.tsx
import { Drawer } from "@mui/material";
import { styled as styled3 } from "@mui/material/styles";

// src/showcase/dashboard/Drawer/StylingMixin.ts
var sideDrawerWidth = 240;
var openedDrawerMixin = (theme) => ({
  width: sideDrawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});
var closedDrawerMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: { width: `calc(${theme.spacing(8)} + 1px)` }
});

// src/showcase/dashboard/Drawer/Styling.tsx
var sideDrawerWidth2 = 240;
var DrawerHeader = styled3("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar
}));
var StyledDrawer = styled3(Drawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme }) => ({
    width: sideDrawerWidth2,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedDrawerMixin(theme),
          "& .MuiDrawer-paper": openedDrawerMixin(theme)
        }
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedDrawerMixin(theme),
          "& .MuiDrawer-paper": closedDrawerMixin(theme)
        }
      }
    ]
  })
);

// src/showcase/dashboard/Drawer/MiniDrawer.tsx
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var MiniDrawer = ({
  menuItems,
  open,
  openHandler
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation2();
  const toggleDrawer = () => {
    openHandler(!open);
  };
  return /* @__PURE__ */ jsxs6(StyledDrawer, { variant: "permanent", open, children: [
    /* @__PURE__ */ jsx6(DrawerHeader, { children: /* @__PURE__ */ jsx6(IconButton2, { onClick: toggleDrawer, children: open ? /* @__PURE__ */ jsx6(ChevronLeftRounded, {}) : /* @__PURE__ */ jsx6(ChevronRightRounded, {}) }) }),
    /* @__PURE__ */ jsx6(Divider, {}),
    /* @__PURE__ */ jsx6(List2, { children: menuItems.map((item) => /* @__PURE__ */ jsx6(
      MenuListItem,
      {
        mode: open ? "full-width" : "icon-only",
        text: item.label,
        icon: item.icon,
        active: pathname === item.path,
        onClick: () => void navigate(item.path)
      },
      item.path
    )) }),
    /* @__PURE__ */ jsx6(FlexGrowCol, {}),
    /* @__PURE__ */ jsx6(Divider, { flexItem: true }),
    /* @__PURE__ */ jsx6(BottomMenuArea, {})
  ] });
};

// src/showcase/dashboard/StandardComponents/AppChrome/AppChrome.tsx
import { Fragment as Fragment2, jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var drawerWidth = 240;
var AppBar = styled4(MuiAppBar, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }
}));
var AppChrome = ({ items }) => {
  const [open, setOpen] = useState3(false);
  const theme = useTheme4();
  const handleDrawerOpen = () => setOpen(true);
  const menuItems = items.map(({ menuItem }) => menuItem);
  const defaultPath = menuItems[0].path;
  const dockedDrawerWidth = theme.components?.MuiDrawer?.styleOverrides?.docked?.width ?? "96px";
  return /* @__PURE__ */ jsxs7(Box, { sx: { display: "flex" }, children: [
    /* @__PURE__ */ jsx7(CssBaseline, {}),
    /* @__PURE__ */ jsx7(AppBar, { position: "fixed", open, children: /* @__PURE__ */ jsxs7(Toolbar2, { disableGutters: true, children: [
      /* @__PURE__ */ jsx7(FlexRow3, { gap: 2, justifyItems: "center", width: dockedDrawerWidth, paddingLeft: "6px", children: /* @__PURE__ */ jsx7(
        IconButton3,
        {
          color: "inherit",
          "aria-label": "open drawer",
          onClick: handleDrawerOpen,
          sx: [
            open && { display: "none" }
          ],
          children: /* @__PURE__ */ jsx7(MenuRounded, {})
        }
      ) }),
      /* @__PURE__ */ jsx7(FlexRow3, { paddingX: 2, children: /* @__PURE__ */ jsx7(Typography2, { variant: "h6", noWrap: true, component: "div", children: "XYO Explorer" }) })
    ] }) }),
    /* @__PURE__ */ jsx7(MiniDrawer, { menuItems, openHandler: setOpen, open }),
    /* @__PURE__ */ jsxs7(
      Box,
      {
        component: "main",
        sx: { flexGrow: 1, p: 3 },
        children: [
          /* @__PURE__ */ jsx7(Toolbar2, {}),
          /* @__PURE__ */ jsxs7(Routes, { children: [
            /* @__PURE__ */ jsx7(Route, { path: "*", element: /* @__PURE__ */ jsx7(Navigate, { to: defaultPath, replace: true }) }),
            items.map(({ menuItem, pageContent }) => /* @__PURE__ */ jsx7(
              Route,
              {
                path: menuItem.path,
                element: /* @__PURE__ */ jsx7(Fragment2, { children: pageContent })
              },
              menuItem.path
            ))
          ] })
        ]
      }
    )
  ] });
};

// src/showcase/dashboard/StandardComponents/DataCard/DataCard.tsx
import {
  Card,
  CardContent,
  CardHeader,
  Typography as Typography3
} from "@mui/material";
import { FlexCol as FlexCol3 } from "@xylabs/react-flexbox";
import { isDefined as isDefined2 } from "@xylabs/typeof";

// src/showcase/dashboard/StandardComponents/DataCard/DataCardSpeedDial.tsx
import {
  FileCopyRounded,
  MoreHorizRounded,
  PrintRounded,
  SaveRounded,
  ShareRounded
} from "@mui/icons-material";
import {
  Box as Box2,
  SpeedDial,
  SpeedDialAction,
  styled as styled5,
  useTheme as useTheme5
} from "@mui/material";
import { animated, useSpring } from "@react-spring/web";
import { useState as useState4 } from "react";
import { jsx as jsx8 } from "react/jsx-runtime";
var actions = [
  { icon: /* @__PURE__ */ jsx8(FileCopyRounded, {}), name: "Copy" },
  { icon: /* @__PURE__ */ jsx8(SaveRounded, {}), name: "Save" },
  { icon: /* @__PURE__ */ jsx8(PrintRounded, {}), name: "Print" },
  { icon: /* @__PURE__ */ jsx8(ShareRounded, {}), name: "Share" }
];
var AnimatedIcon = ({ open, size }) => {
  const { transform } = useSpring({
    transform: open ? "rotate(90deg)" : "rotate(0deg)",
    config: { tension: 200, friction: 15 }
  });
  return /* @__PURE__ */ jsx8(
    animated.div,
    {
      style: {
        transform,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transformOrigin: "center"
      },
      children: /* @__PURE__ */ jsx8(MoreHorizRounded, { fontSize: size })
    }
  );
};
var StyledSpeedDial = styled5(SpeedDial)(({ theme }) => ({
  "position": "absolute",
  "top": "0",
  "right": "0",
  "& .MuiSpeedDial-fab": {
    "backgroundColor": "transparent",
    "color": theme.vars.palette.text.primary,
    "boxShadow": "none",
    "minWidth": "auto",
    "minHeight": "auto",
    "&:hover": { backgroundColor: "transparent" }
  }
}));
var DataCardSpeedDial = ({ size }) => {
  const [open, setOpen] = useState4(false);
  const theme = useTheme5();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return /* @__PURE__ */ jsx8(Box2, { position: "relative", minWidth: "40px", minHeight: "40px", children: /* @__PURE__ */ jsx8(
    StyledSpeedDial,
    {
      ariaLabel: "Data Card Actions",
      icon: /* @__PURE__ */ jsx8(AnimatedIcon, { open }),
      FabProps: { size },
      direction: "down",
      onClose: handleClose,
      onOpen: handleOpen,
      open,
      sx: { zIndex: open ? 1300 : 1 },
      children: actions.map((action) => /* @__PURE__ */ jsx8(
        SpeedDialAction,
        {
          icon: action.icon,
          tooltipTitle: action.name,
          sx: {
            "backgroundColor": theme.vars.palette.primary.main,
            "color": theme.vars.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.vars.palette.primary.light }
          }
        },
        action.name
      ))
    }
  ) });
};

// src/showcase/dashboard/StandardComponents/DataCard/DataCard.tsx
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var DataCard = ({
  headerTitle,
  cardContent,
  dataDescription,
  size = "medium"
}) => {
  return /* @__PURE__ */ jsxs8(
    Card,
    {
      sx: {
        overflow: "visible",
        display: "flex",
        height: "100%",
        justifyContent: "flex-start"
      },
      children: [
        /* @__PURE__ */ jsx9(
          CardHeader,
          {
            width: "80%",
            title: /* @__PURE__ */ jsx9(
              Typography3,
              {
                noWrap: true,
                width: "90%",
                variant: "body2",
                textOverflow: "ellipsis",
                children: headerTitle
              }
            ),
            action: /* @__PURE__ */ jsx9(DataCardSpeedDial, { size })
          }
        ),
        isDefined2(dataDescription) ? /* @__PURE__ */ jsx9(CardContent, { sx: { paddingY: 0 }, children: /* @__PURE__ */ jsx9(FlexCol3, { alignItems: "stretch", children: /* @__PURE__ */ jsx9(Typography3, { textAlign: "justify", width: "100%", variant: "caption", children: dataDescription }) }) }) : null,
        /* @__PURE__ */ jsx9(CardContent, { children: /* @__PURE__ */ jsx9(FlexCol3, { alignItems: "stretch", children: cardContent }) })
      ]
    }
  );
};

// src/showcase/dashboard/DataViewers/Charts/LineCharts/DailyVerificationLineChart.tsx
import { useTheme as useTheme6 } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as Tooltip2,
  XAxis,
  YAxis
} from "recharts";

// src/showcase/dashboard/DataViewers/Charts/Shared/CustomTooltip.tsx
import { Card as Card2, Typography as Typography4 } from "@mui/material";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var CustomDataTooltip = ({
  active,
  payload,
  tooltipData
}) => {
  if (!active || !payload || payload.length === 0) return null;
  return /* @__PURE__ */ jsx10(Card2, { variant: "outlined", sx: { p: 1 }, children: tooltipData.map(({ label, dataKey }, index) => {
    const value = payload[0].payload[dataKey] ?? "N/A";
    return /* @__PURE__ */ jsxs9(Typography4, { variant: "body2", children: [
      /* @__PURE__ */ jsxs9("strong", { children: [
        label,
        ":"
      ] }),
      " ",
      value
    ] }, index);
  }) });
};

// src/showcase/dashboard/DataViewers/Charts/Shared/DataProps.tsx
var generateVerificationData = (count, min, max, chartType) => {
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return Array.from({ length: count }, () => {
    const randomVerifications = Math.floor(Math.random() * (max - min + 1)) + min;
    return chartType === "line-day" ? {
      day: daysOfWeek[Math.floor(Math.random() * daysOfWeek.length)],
      verifications: randomVerifications
    } : {
      time: Math.floor(Math.random() * 24),
      // random hour of the day (0-23)
      verifications: randomVerifications
    };
  });
};

// src/showcase/dashboard/DataViewers/Charts/LineCharts/DailyVerificationLineChart.tsx
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var DailyVerificationLineChart = ({ data }) => {
  const theme = useTheme6();
  return /* @__PURE__ */ jsx11(ResponsiveContainer, { width: "100%", height: 400, children: /* @__PURE__ */ jsxs10(
    AreaChart,
    {
      data,
      margin: {
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      },
      children: [
        /* @__PURE__ */ jsx11(CartesianGrid, { stroke: theme.vars.palette.background.paper }),
        /* @__PURE__ */ jsx11(XAxis, { dataKey: "day" }),
        /* @__PURE__ */ jsx11(YAxis, { width: 20 }),
        /* @__PURE__ */ jsx11(
          Tooltip2,
          {
            content: /* @__PURE__ */ jsx11(
              CustomDataTooltip,
              {
                tooltipData: [
                  { label: "Day", dataKey: "day" },
                  { label: "Verifications", dataKey: "verifications" }
                ]
              }
            ),
            cursor: { strokeDasharray: "3 3" }
          }
        ),
        /* @__PURE__ */ jsx11(
          Area,
          {
            type: "monotone",
            dataKey: "verifications",
            stackId: "1",
            stroke: theme.vars.palette.primary.main,
            fill: theme.vars.palette.primary.light
          }
        )
      ]
    }
  ) });
};

// src/showcase/dashboard/DataViewers/Charts/LineCharts/DailyVerificationStackedAreaChart.tsx
import { useTheme as useTheme7 } from "@mui/material";
import {
  Area as Area2,
  AreaChart as AreaChart2,
  CartesianGrid as CartesianGrid2,
  ResponsiveContainer as ResponsiveContainer2,
  Tooltip as Tooltip3,
  XAxis as XAxis2,
  YAxis as YAxis2
} from "recharts";
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
var DailyVerificationStackedAreaChart = ({ data }) => {
  const theme = useTheme7();
  return /* @__PURE__ */ jsx12(ResponsiveContainer2, { width: "100%", height: 400, children: /* @__PURE__ */ jsxs11(
    AreaChart2,
    {
      data,
      margin: {
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      },
      children: [
        /* @__PURE__ */ jsx12(CartesianGrid2, { stroke: theme.vars.palette.background.paper }),
        /* @__PURE__ */ jsx12(XAxis2, { dataKey: "day" }),
        /* @__PURE__ */ jsx12(YAxis2, { width: 20 }),
        /* @__PURE__ */ jsx12(
          Tooltip3,
          {
            content: /* @__PURE__ */ jsx12(
              CustomDataTooltip,
              {
                tooltipData: [
                  { label: "Day", dataKey: "day" },
                  { label: "Verifications", dataKey: "verifications" }
                ]
              }
            ),
            cursor: { strokeDasharray: "3 3" }
          }
        ),
        /* @__PURE__ */ jsx12(
          Area2,
          {
            type: "monotone",
            dataKey: "mobile",
            stackId: "1",
            stroke: theme.vars.palette.primary.main,
            fill: theme.vars.palette.primary.main
          }
        ),
        /* @__PURE__ */ jsx12(
          Area2,
          {
            type: "monotone",
            dataKey: "web",
            stackId: "1",
            stroke: theme.vars.palette.secondary.main,
            fill: theme.vars.palette.secondary.light
          }
        ),
        /* @__PURE__ */ jsx12(
          Area2,
          {
            type: "monotone",
            dataKey: "email",
            stackId: "1",
            stroke: theme.vars.palette.success.main,
            fill: theme.vars.palette.success.light
          }
        )
      ]
    }
  ) });
};

// src/showcase/dashboard/DataViewers/Charts/LineCharts/sampleVerificationData.ts
var sampleVerificationData_exports = {};
__export(sampleVerificationData_exports, {
  employeeCheckInsData: () => employeeCheckInsData,
  stackedVerificationData: () => stackedVerificationData
});
var employeeCheckInsData = [
  { day: "Monday", verifications: 120 },
  { day: "Tuesday", verifications: 200 },
  { day: "Wednesday", verifications: 150 },
  { day: "Thursday", verifications: 300 },
  { day: "Friday", verifications: 250 }
];
var stackedVerificationData = [
  {
    day: "Monday",
    mobile: 120,
    web: 90,
    email: 50
  },
  {
    day: "Tuesday",
    mobile: 200,
    web: 130,
    email: 80
  },
  {
    day: "Wednesday",
    mobile: 150,
    web: 100,
    email: 60
  },
  {
    day: "Thursday",
    mobile: 300,
    web: 200,
    email: 100
  },
  {
    day: "Friday",
    mobile: 250,
    web: 150,
    email: 70
  }
];

// src/showcase/dashboard/DataViewers/Charts/PieCharts/DualRingPieChart.tsx
import {
  Box as Box3,
  Typography as Typography5,
  useTheme as useTheme8
} from "@mui/material";
import { FlexRow as FlexRow4 } from "@xylabs/react-flexbox";
import { isDefined as isDefined3 } from "@xylabs/typeof";
import { useState as useState5 } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer as ResponsiveContainer3,
  Tooltip as Tooltip4
} from "recharts";
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var StyledDualRingPieChart = ({
  data01,
  data02,
  showLegend = false
}) => {
  const theme = useTheme8();
  const [activeIndex, setActiveIndex] = useState5(null);
  const handleMouseEnter = (index) => {
    setActiveIndex(index);
  };
  const handleMouseLeave = () => {
    setActiveIndex(null);
  };
  const colors = [
    alphaCss(theme.vars.palette.primary.main, 0.8),
    alphaCss(theme.vars.palette.secondary.main, 0.8),
    alphaCss(theme.vars.palette.error.main, 0.8),
    alphaCss(theme.vars.palette.warning.main, 0.8),
    alphaCss(theme.vars.palette.success.main, 0.8)
  ];
  return /* @__PURE__ */ jsxs12(Box3, { display: "flex", flexDirection: "column", alignItems: "center", children: [
    showLegend && /* @__PURE__ */ jsx13(Box3, { display: "flex", flexDirection: "column", mb: 2, children: data01.map((entry, index) => /* @__PURE__ */ jsxs12(FlexRow4, { gap: 1, alignItems: "center", children: [
      /* @__PURE__ */ jsx13(
        Box3,
        {
          sx: {
            backgroundColor: colors[index % colors.length],
            borderRadius: 1,
            height: "20px",
            width: "20px"
          }
        }
      ),
      /* @__PURE__ */ jsx13(Typography5, { variant: "body2", children: entry.name })
    ] }, index)) }),
    /* @__PURE__ */ jsx13(ResponsiveContainer3, { width: "100%", height: 400, children: /* @__PURE__ */ jsxs12(PieChart, { children: [
      /* @__PURE__ */ jsx13(
        Tooltip4,
        {
          content: /* @__PURE__ */ jsx13(
            CustomDataTooltip,
            {
              tooltipData: [
                { label: "Name", dataKey: "name" },
                { label: "Value", dataKey: "value" }
              ]
            }
          )
        }
      ),
      /* @__PURE__ */ jsx13(
        Pie,
        {
          data: data01,
          dataKey: "value",
          nameKey: "name",
          cx: "50%",
          cy: "50%",
          innerRadius: 60,
          outerRadius: 80,
          startAngle: 90,
          endAngle: -270,
          label: ({ value }) => `${value}`,
          labelLine: false,
          paddingAngle: 5,
          cornerRadius: 3,
          stroke: "none",
          children: data01.map((entry, index) => /* @__PURE__ */ jsx13(
            Cell,
            {
              fill: colors[index % colors.length],
              onMouseEnter: () => handleMouseEnter(index),
              onMouseLeave: handleMouseLeave
            },
            `cell-${index}`
          ))
        }
      ),
      isDefined3(data02) && /* @__PURE__ */ jsx13(
        Pie,
        {
          data: data02,
          dataKey: "value",
          nameKey: "name",
          cx: "50%",
          cy: "50%",
          innerRadius: 85,
          outerRadius: 100,
          startAngle: 10,
          endAngle: -350,
          label: ({ value }) => `${value}`,
          labelLine: false,
          paddingAngle: 5,
          cornerRadius: 3,
          stroke: "none",
          children: data02.map((entry, index) => /* @__PURE__ */ jsx13(
            Cell,
            {
              fill: colors[index % colors.length],
              onMouseEnter: () => handleMouseEnter(index),
              onMouseLeave: handleMouseLeave
            },
            `cell-${index}`
          ))
        }
      )
    ] }) })
  ] });
};

// src/showcase/dashboard/DataViewers/Charts/ScatterCharts/DailyVerificationScatterChart.tsx
import {
  alpha as alpha6,
  darken as darken2,
  useTheme as useTheme9
} from "@mui/material";
import { useState as useState6 } from "react";
import {
  CartesianGrid as CartesianGrid3,
  Cell as Cell2,
  ReferenceLine,
  ResponsiveContainer as ResponsiveContainer4,
  Scatter,
  ScatterChart,
  Tooltip as Tooltip5,
  XAxis as XAxis3,
  YAxis as YAxis3
} from "recharts";
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var getVerificationColor = (verifications) => {
  if (verifications >= 30) return "#006EF5";
  if (verifications < 5) return alpha6("#EF233C", 5 / verifications / 2);
  return alpha6("#006EF5", verifications / 30 / 3);
};
var DailyVerificationScatterChart = ({ data }) => {
  const [isHovered, setIsHovered] = useState6(false);
  const [tooltipPos, setTooltipPos] = useState6({ x: 0, y: 0 });
  const theme = useTheme9();
  const handleReferenceLineMouseMove = (event) => {
    setTooltipPos({ x: event.clientX - 60, y: event.clientY - 80 });
    setIsHovered(true);
  };
  const handleReferenceLineMouseLeave = () => {
    setIsHovered(false);
  };
  return /* @__PURE__ */ jsxs13("div", { style: { position: "relative" }, children: [
    isHovered && /* @__PURE__ */ jsx14(
      CustomDataTooltip,
      {
        sx: {
          position: "absolute",
          top: tooltipPos.y,
          left: tooltipPos.x,
          p: 1,
          backgroundColor: "white",
          border: "1px solid #ddd",
          borderRadius: 1,
          zIndex: 10,
          pointerEvents: "none"
        },
        tooltipData: [
          { label: "Threshold", dataKey: "30 Verifications" }
        ]
      }
    ),
    /* @__PURE__ */ jsx14(ResponsiveContainer4, { width: "100%", height: 400, children: /* @__PURE__ */ jsxs13(
      ScatterChart,
      {
        margin: {
          top: 20,
          right: 20,
          bottom: 20,
          left: 20
        },
        children: [
          /* @__PURE__ */ jsx14(CartesianGrid3, { vertical: false, stroke: lightenCss(theme.vars.palette.background.paper, 0.2) }),
          /* @__PURE__ */ jsx14(
            XAxis3,
            {
              type: "number",
              dataKey: "time",
              name: "Time of Day",
              unit: ":00",
              domain: [8, 17],
              tickCount: 10,
              tickLine: true,
              tick: { fontSize: theme.typography.body2.fontSize }
            }
          ),
          /* @__PURE__ */ jsx14(
            YAxis3,
            {
              width: 20,
              type: "number",
              dataKey: "verifications",
              name: "Verifications",
              tickLine: true,
              tick: { fontSize: theme.typography.body2.fontSize },
              orientation: "right",
              axisLine: false
            }
          ),
          /* @__PURE__ */ jsx14(
            Tooltip5,
            {
              content: /* @__PURE__ */ jsx14(
                CustomDataTooltip,
                {
                  tooltipData: [
                    { label: "Time", dataKey: "time" },
                    { label: "Verifications", dataKey: "verifications" }
                  ]
                }
              ),
              cursor: { strokeDasharray: "3 3" }
            }
          ),
          /* @__PURE__ */ jsx14(
            ReferenceLine,
            {
              y: 30,
              stroke: isHovered ? darken2("#006EF5", 0.3) : alpha6("#006EF5", 0.7),
              strokeWidth: isHovered ? 2 : 1
            }
          ),
          /* @__PURE__ */ jsx14(
            ReferenceLine,
            {
              y: 30,
              stroke: "transparent",
              strokeWidth: 10,
              onMouseMove: handleReferenceLineMouseMove,
              onMouseLeave: handleReferenceLineMouseLeave
            }
          ),
          /* @__PURE__ */ jsx14(
            Scatter,
            {
              name: "Verification Scatter",
              data,
              shape: "circle",
              isAnimationActive: false,
              children: data.map((entry, index) => /* @__PURE__ */ jsx14(Cell2, { fill: getVerificationColor(entry.verifications) }, `cell-${index}`))
            }
          )
        ]
      }
    ) })
  ] });
};

// src/showcase/dashboard/DataViewers/Charts/ScatterCharts/sampleVerificationData.ts
var sampleVerificationData_exports2 = {};
__export(sampleVerificationData_exports2, {
  employeeCheckInsData: () => employeeCheckInsData2,
  mobileAppQuestCheckInsData: () => mobileAppQuestCheckInsData,
  productDeliveryCheckInsData: () => productDeliveryCheckInsData
});
var employeeCheckInsData2 = [
  { time: 8, verifications: 5 },
  { time: 8, verifications: 8 },
  { time: 8, verifications: 12 },
  { time: 8, verifications: 18 },
  { time: 8, verifications: 20 },
  { time: 8, verifications: 25 },
  { time: 9, verifications: 15 },
  { time: 9, verifications: 20 },
  { time: 9, verifications: 22 },
  { time: 9, verifications: 28 },
  { time: 9, verifications: 35 },
  { time: 9, verifications: 40 },
  { time: 10, verifications: 2 },
  { time: 10, verifications: 12 },
  { time: 10, verifications: 15 },
  { time: 10, verifications: 18 },
  { time: 10, verifications: 22 },
  { time: 10, verifications: 30 },
  { time: 11, verifications: 35 },
  { time: 11, verifications: 37 },
  { time: 11, verifications: 40 },
  { time: 11, verifications: 45 },
  { time: 11, verifications: 50 },
  { time: 11, verifications: 55 },
  { time: 12, verifications: 25 },
  { time: 12, verifications: 30 },
  { time: 12, verifications: 28 },
  { time: 12, verifications: 35 },
  { time: 12, verifications: 40 },
  { time: 12, verifications: 45 },
  { time: 13, verifications: 18 },
  { time: 13, verifications: 28 },
  { time: 13, verifications: 32 },
  { time: 13, verifications: 37 },
  { time: 13, verifications: 45 },
  { time: 13, verifications: 50 },
  { time: 14, verifications: 3 },
  { time: 14, verifications: 7 },
  { time: 14, verifications: 10 },
  { time: 14, verifications: 12 },
  { time: 14, verifications: 15 },
  { time: 14, verifications: 18 },
  { time: 15, verifications: 30 },
  { time: 15, verifications: 32 },
  { time: 15, verifications: 34 },
  { time: 15, verifications: 37 },
  { time: 15, verifications: 40 },
  { time: 15, verifications: 45 },
  { time: 16, verifications: 8 },
  { time: 16, verifications: 10 },
  { time: 16, verifications: 12 },
  { time: 16, verifications: 15 },
  { time: 16, verifications: 18 },
  { time: 16, verifications: 22 },
  { time: 17, verifications: 22 },
  { time: 17, verifications: 24 },
  { time: 17, verifications: 27 },
  { time: 17, verifications: 30 },
  { time: 17, verifications: 35 },
  { time: 17, verifications: 40 },
  { time: 18, verifications: 16 },
  { time: 18, verifications: 18 },
  { time: 18, verifications: 19 },
  { time: 18, verifications: 22 },
  { time: 18, verifications: 24 },
  { time: 18, verifications: 28 },
  { time: 19, verifications: 10 },
  { time: 19, verifications: 8 },
  { time: 19, verifications: 12 },
  { time: 19, verifications: 15 },
  { time: 19, verifications: 18 },
  { time: 19, verifications: 22 },
  { time: 20, verifications: 5 },
  { time: 20, verifications: 9 },
  { time: 20, verifications: 14 },
  { time: 20, verifications: 16 },
  { time: 20, verifications: 18 },
  { time: 20, verifications: 20 },
  { time: 21, verifications: 25 },
  { time: 21, verifications: 27 },
  { time: 21, verifications: 30 },
  { time: 21, verifications: 35 },
  { time: 21, verifications: 40 },
  { time: 21, verifications: 45 }
];
var productDeliveryCheckInsData = [
  { time: 8, verifications: 10 },
  { time: 8, verifications: 5 },
  { time: 8, verifications: 6 },
  { time: 8, verifications: 8 },
  { time: 8, verifications: 9 },
  { time: 8, verifications: 12 },
  { time: 9, verifications: 0 },
  { time: 9, verifications: 3 },
  { time: 9, verifications: 8 },
  { time: 9, verifications: 10 },
  { time: 9, verifications: 15 },
  { time: 9, verifications: 18 },
  { time: 10, verifications: 20 },
  { time: 10, verifications: 22 },
  { time: 10, verifications: 25 },
  { time: 10, verifications: 28 },
  { time: 10, verifications: 30 },
  { time: 10, verifications: 32 },
  { time: 11, verifications: 12 },
  { time: 11, verifications: 17 },
  { time: 11, verifications: 19 },
  { time: 11, verifications: 22 },
  { time: 11, verifications: 25 },
  { time: 11, verifications: 30 },
  { time: 12, verifications: 2 },
  { time: 12, verifications: 8 },
  { time: 12, verifications: 10 },
  { time: 12, verifications: 15 },
  { time: 12, verifications: 20 },
  { time: 12, verifications: 25 },
  { time: 13, verifications: 30 },
  { time: 13, verifications: 35 },
  { time: 13, verifications: 36 },
  { time: 13, verifications: 40 },
  { time: 13, verifications: 42 },
  { time: 13, verifications: 45 },
  { time: 14, verifications: 8 },
  { time: 14, verifications: 12 },
  { time: 14, verifications: 14 },
  { time: 14, verifications: 15 },
  { time: 14, verifications: 18 },
  { time: 14, verifications: 20 },
  { time: 15, verifications: 40 },
  { time: 15, verifications: 45 },
  { time: 15, verifications: 43 },
  { time: 15, verifications: 48 },
  { time: 15, verifications: 50 },
  { time: 15, verifications: 55 },
  { time: 16, verifications: 5 },
  { time: 16, verifications: 7 },
  { time: 16, verifications: 9 },
  { time: 16, verifications: 12 },
  { time: 16, verifications: 15 },
  { time: 16, verifications: 20 },
  { time: 17, verifications: 15 },
  { time: 17, verifications: 18 },
  { time: 17, verifications: 20 },
  { time: 17, verifications: 25 },
  { time: 17, verifications: 28 },
  { time: 17, verifications: 30 },
  { time: 18, verifications: 10 },
  { time: 18, verifications: 12 },
  { time: 18, verifications: 15 },
  { time: 18, verifications: 18 },
  { time: 18, verifications: 22 },
  { time: 18, verifications: 25 },
  { time: 19, verifications: 22 },
  { time: 19, verifications: 24 },
  { time: 19, verifications: 25 },
  { time: 19, verifications: 28 },
  { time: 19, verifications: 30 },
  { time: 19, verifications: 35 },
  { time: 20, verifications: 8 },
  { time: 20, verifications: 12 },
  { time: 20, verifications: 14 },
  { time: 20, verifications: 18 },
  { time: 20, verifications: 20 },
  { time: 20, verifications: 22 },
  { time: 21, verifications: 27 },
  { time: 21, verifications: 30 },
  { time: 21, verifications: 32 },
  { time: 21, verifications: 35 },
  { time: 21, verifications: 38 },
  { time: 21, verifications: 40 }
];
var mobileAppQuestCheckInsData = [
  { time: 8, verifications: 1 },
  { time: 8, verifications: 2 },
  { time: 8, verifications: 3 },
  { time: 8, verifications: 5 },
  { time: 8, verifications: 7 },
  { time: 8, verifications: 9 },
  { time: 9, verifications: 5 },
  { time: 9, verifications: 6 },
  { time: 9, verifications: 7 },
  { time: 9, verifications: 9 },
  { time: 9, verifications: 10 },
  { time: 9, verifications: 12 },
  { time: 10, verifications: 3 },
  { time: 10, verifications: 7 },
  { time: 10, verifications: 9 },
  { time: 10, verifications: 11 },
  { time: 10, verifications: 13 },
  { time: 10, verifications: 15 },
  { time: 11, verifications: 2 },
  { time: 11, verifications: 4 },
  { time: 11, verifications: 6 },
  { time: 11, verifications: 8 },
  { time: 11, verifications: 10 },
  { time: 11, verifications: 12 },
  { time: 12, verifications: 4 },
  { time: 12, verifications: 9 },
  { time: 12, verifications: 11 },
  { time: 12, verifications: 13 },
  { time: 12, verifications: 15 },
  { time: 12, verifications: 18 },
  { time: 13, verifications: 7 },
  { time: 13, verifications: 10 },
  { time: 13, verifications: 12 },
  { time: 13, verifications: 15 },
  { time: 13, verifications: 17 },
  { time: 13, verifications: 20 },
  { time: 14, verifications: 9 },
  { time: 14, verifications: 11 },
  { time: 14, verifications: 13 },
  { time: 14, verifications: 15 },
  { time: 14, verifications: 17 },
  { time: 14, verifications: 19 },
  { time: 15, verifications: 8 },
  { time: 15, verifications: 12 },
  { time: 15, verifications: 14 },
  { time: 15, verifications: 16 },
  { time: 15, verifications: 18 },
  { time: 15, verifications: 22 },
  { time: 16, verifications: 30 },
  { time: 16, verifications: 32 },
  { time: 16, verifications: 35 },
  { time: 16, verifications: 37 },
  { time: 16, verifications: 40 },
  { time: 16, verifications: 42 },
  { time: 17, verifications: 25 },
  { time: 17, verifications: 28 },
  { time: 17, verifications: 27 },
  { time: 17, verifications: 30 },
  { time: 17, verifications: 32 },
  { time: 17, verifications: 35 },
  { time: 18, verifications: 10 },
  { time: 18, verifications: 8 },
  { time: 18, verifications: 12 },
  { time: 18, verifications: 15 },
  { time: 18, verifications: 18 },
  { time: 18, verifications: 20 },
  { time: 19, verifications: 20 },
  { time: 19, verifications: 18 },
  { time: 19, verifications: 15 },
  { time: 19, verifications: 17 },
  { time: 19, verifications: 19 },
  { time: 19, verifications: 22 },
  { time: 20, verifications: 7 },
  { time: 20, verifications: 5 },
  { time: 20, verifications: 8 },
  { time: 20, verifications: 10 },
  { time: 20, verifications: 12 },
  { time: 20, verifications: 15 },
  { time: 21, verifications: 11 },
  { time: 21, verifications: 9 },
  { time: 21, verifications: 12 },
  { time: 21, verifications: 15 },
  { time: 21, verifications: 18 },
  { time: 21, verifications: 20 }
];

// src/showcase/dashboard/DataViewers/DataHealth.tsx
import { ArrowDownwardRounded, ArrowUpwardRounded } from "@mui/icons-material";
import {
  Box as Box4,
  Card as Card3,
  CardContent as CardContent2,
  CircularProgress,
  Fade,
  Stack,
  Tooltip as Tooltip6,
  Typography as Typography6
} from "@mui/material";
import { FlexCol as FlexCol4, FlexRow as FlexRow5 } from "@xylabs/react-flexbox";
import { useEffect, useState as useState7 } from "react";

// src/showcase/dashboard/DataViewers/img/index.ts
import { default as default2 } from "./earth-NLYJZ2YQ.webp";

// src/showcase/dashboard/DataViewers/DataHealth.tsx
import { jsx as jsx15, jsxs as jsxs14 } from "react/jsx-runtime";
var DataHealth = ({
  cardTitle,
  comparisonText,
  comparisonValue,
  status
}) => {
  const [loading, setLoading] = useState7(true);
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2e3);
    return () => clearTimeout(timer);
  }, []);
  return /* @__PURE__ */ jsx15(Box4, { children: /* @__PURE__ */ jsxs14(
    Card3,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderRadius: 2,
        flexDirection: "column"
      },
      children: [
        /* @__PURE__ */ jsx15(CardContent2, { sx: { textAlign: "center" }, children: /* @__PURE__ */ jsxs14(FlexCol4, { alignItems: "center", children: [
          /* @__PURE__ */ jsx15(Typography6, { variant: "h5", gutterBottom: true, paddingBottom: 1, children: cardTitle }),
          loading ? /* @__PURE__ */ jsx15(Fade, { in: loading, children: /* @__PURE__ */ jsxs14(FlexRow5, { gap: 1, alignItems: "center", children: [
            /* @__PURE__ */ jsx15(CircularProgress, { size: 16, sx: { color: "#00bcd4", mr: 1 } }),
            /* @__PURE__ */ jsx15(Typography6, { variant: "body1", children: status })
          ] }) }) : /* @__PURE__ */ jsx15(Fade, { in: !loading, children: /* @__PURE__ */ jsx15(
            Tooltip6,
            {
              title: /* @__PURE__ */ jsx15(Typography6, { variant: "body2", children: comparisonText }),
              children: /* @__PURE__ */ jsxs14(FlexCol4, { gap: 1, alignItems: "center", children: [
                /* @__PURE__ */ jsx15(Typography6, { variant: "h4", children: "97%" }),
                /* @__PURE__ */ jsxs14(Stack, { flexDirection: "row", alignItems: "center", gap: 1, children: [
                  /* @__PURE__ */ jsx15(Typography6, { children: /* @__PURE__ */ jsx15("strong", { children: comparisonValue >= 0 ? `+${comparisonValue.toFixed(2)}%` : `${comparisonValue.toFixed(2)}%` }) }),
                  comparisonValue >= 0 ? /* @__PURE__ */ jsx15(ArrowUpwardRounded, { fontSize: "small" }) : /* @__PURE__ */ jsx15(ArrowDownwardRounded, { fontSize: "small" })
                ] })
              ] })
            }
          ) })
        ] }) }),
        /* @__PURE__ */ jsx15(
          Box4,
          {
            sx: {
              justifySelf: "center",
              backgroundImage: `url(${default2})`,
              backgroundSize: "400px",
              backgroundPosition: "top center",
              backgroundRepeat: "no-repeat",
              height: "200px",
              width: "120%"
            }
          }
        )
      ]
    }
  ) });
};

// src/showcase/dashboard/DataViewers/VerificationSummary/VerificationList.tsx
import {
  Box as Box5,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography as Typography7,
  useTheme as useTheme10
} from "@mui/material";
import { jsx as jsx16, jsxs as jsxs15 } from "react/jsx-runtime";
var VerificationList = ({
  regions,
  locationHeader,
  timeHeader,
  statusHeader
}) => {
  const theme = useTheme10();
  return /* @__PURE__ */ jsx16(Box5, { children: /* @__PURE__ */ jsxs15(Table, { size: "small", children: [
    /* @__PURE__ */ jsx16(TableHead, { children: /* @__PURE__ */ jsxs15(TableRow, { children: [
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(Typography7, { variant: "body2", fontWeight: "bold", children: locationHeader }) }),
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(Typography7, { variant: "body2", fontWeight: "bold", children: timeHeader }) }),
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(Typography7, { variant: "body2", fontWeight: "bold", children: statusHeader }) })
    ] }) }),
    /* @__PURE__ */ jsx16(TableBody, { children: regions.map((region, index) => /* @__PURE__ */ jsxs15(TableRow, { sx: { borderBottom: index === regions.length - 1 ? "none" : "inherit" }, children: [
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(Typography7, { variant: "body2", children: region.name }) }),
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(Typography7, { variant: "body2", children: region.time }) }),
      /* @__PURE__ */ jsx16(TableCell, { children: /* @__PURE__ */ jsx16(
        Typography7,
        {
          variant: "body2",
          color: region.verified ? theme.vars.palette.success.main : theme.vars.palette.error.main,
          children: region.verified ? "Verified" : "Unverified"
        }
      ) })
    ] }, index)) })
  ] }) });
};

// src/showcase/dashboard/DataViewers/VerificationSummary/VerificationSummaryProps.tsx
import {
  Box as Box7,
  Typography as Typography10
} from "@mui/material";
import { FlexCol as FlexCol6 } from "@xylabs/react-flexbox";

// src/showcase/dashboard/StandardComponents/DropdownSelect/DropdownSelect.tsx
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import {
  Box as Box6,
  FormControl,
  MenuItem,
  Select,
  Typography as Typography8,
  useTheme as useTheme11
} from "@mui/material";
import { FlexRow as FlexRow6 } from "@xylabs/react-flexbox";
import { useMemo as useMemo2, useState as useState8 } from "react";
import { jsx as jsx17, jsxs as jsxs16 } from "react/jsx-runtime";
var DropdownSelect = ({
  items,
  onChange,
  selectedValue,
  size
}) => {
  const theme = useTheme11();
  const [localSelectedValue, setLocalSelectedValue] = useState8(selectedValue);
  useMemo2(() => {
    setLocalSelectedValue(selectedValue);
  }, [selectedValue]);
  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalSelectedValue(newValue);
    onChange(newValue);
  };
  return /* @__PURE__ */ jsx17(Box6, { sx: { minWidth: 200 }, children: /* @__PURE__ */ jsx17(FormControl, { fullWidth: true, children: /* @__PURE__ */ jsx17(
    Select,
    {
      color: "secondary",
      labelId: "styled-select-label",
      id: "styled-select",
      size,
      value: localSelectedValue,
      onChange: handleChange,
      IconComponent: KeyboardArrowDownRounded,
      inputProps: { color: theme.vars.palette.text.primary },
      MenuProps: {
        PaperProps: {
          sx: {
            "marginTop": 1,
            "& .Mui-selected": {
              backgroundColor: alphaCss(theme.vars.palette.secondary.main, 0.1) + " !important",
              color: theme.vars.palette.secondary.main
            },
            "& .MuiList-root": { padding: 0 },
            "& .MuiMenuItem-root": {
              "display": "flex",
              "alignItems": "center",
              "gap": 1,
              "marginX": size === "small" ? 0.5 : 1,
              "marginY": size === "small" ? 0.5 : 1,
              "padding": size === "small" ? 0.75 : 1,
              "borderRadius": 1,
              "border": `${alphaCss(theme.vars.palette.secondary.main, 0)} 2px solid`,
              "&:hover": {
                backgroundColor: alphaCss(theme.vars.palette.secondary.main, 0),
                border: `${theme.vars.palette.secondary.main} 2px solid`,
                color: theme.vars.palette.secondary.main
              }
            }
          }
        }
      },
      children: items.map((item) => /* @__PURE__ */ jsx17(MenuItem, { value: item.value, children: /* @__PURE__ */ jsxs16(FlexRow6, { gap: 1, justifyContent: "flex-start", children: [
        item.icon,
        /* @__PURE__ */ jsx17(Typography8, { variant: "body1", fontWeight: 400, children: item.text })
      ] }) }, item.value))
    }
  ) }) });
};

// src/showcase/dashboard/StandardComponents/DropdownSelect/DropdownSelectData.tsx
import {
  CardGiftcard,
  Event,
  LocalShipping,
  Map,
  People,
  PhoneAndroid,
  Public,
  SportsEsports,
  VerifiedUser
} from "@mui/icons-material";
import { jsx as jsx18 } from "react/jsx-runtime";
var DropdownSelectCategories = [
  {
    icon: /* @__PURE__ */ jsx18(Map, {}),
    text: "Data Mapping",
    value: "dataMapping"
  },
  {
    icon: /* @__PURE__ */ jsx18(Public, {}),
    text: "DePIN",
    value: "depin"
  },
  {
    icon: /* @__PURE__ */ jsx18(People, {}),
    text: "Employee Tracking",
    value: "employeeTracking"
  },
  {
    icon: /* @__PURE__ */ jsx18(Event, {}),
    text: "Event Attendance",
    value: "eventAttendance"
  },
  {
    icon: /* @__PURE__ */ jsx18(SportsEsports, {}),
    text: "GameFi",
    value: "gamefi"
  },
  {
    icon: /* @__PURE__ */ jsx18(PhoneAndroid, {}),
    text: "Mobile Apps",
    value: "mobileApp"
  },
  {
    icon: /* @__PURE__ */ jsx18(VerifiedUser, {}),
    text: "Partner Validation",
    value: "partnerValidation"
  },
  {
    icon: /* @__PURE__ */ jsx18(CardGiftcard, {}),
    text: "Reward Programs",
    value: "rewardPrograms"
  },
  {
    icon: /* @__PURE__ */ jsx18(LocalShipping, {}),
    text: "Supply Chain",
    value: "supplyChain"
  }
];

// src/showcase/dashboard/StandardComponents/StatCard/StatCard.tsx
import { ArrowDownwardRounded as ArrowDownwardRounded2, ArrowUpwardRounded as ArrowUpwardRounded2 } from "@mui/icons-material";
import {
  Tooltip as Tooltip7,
  Typography as Typography9,
  useTheme as useTheme12
} from "@mui/material";
import { FlexCol as FlexCol5, FlexRow as FlexRow7 } from "@xylabs/react-flexbox";
import { isDefined as isDefined4 } from "@xylabs/typeof";
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var formatNumber = (num) => {
  if (num >= 1e6) {
    return `${(num / 1e6).toFixed(1)}m`;
  }
  if (num >= 1e3) {
    return `${(num / 1e3).toFixed(1)}k`;
  }
  return num.toString();
};
var StatCard = ({
  headerTitle,
  value,
  change,
  description,
  customUnit,
  reverseDirectionality = false,
  ...props
}) => {
  const theme = useTheme12();
  const isPositive = change >= 0;
  const changeColor = isPositive && !reverseDirectionality ? theme.vars.palette.success.main : isPositive && reverseDirectionality ? theme.vars.palette.error.main : theme.vars.palette.success.main;
  const formattedValue = formatNumber(value);
  const showTooltip = formattedValue !== value.toString();
  return /* @__PURE__ */ jsx19(
    DataCard,
    {
      headerTitle,
      ...props,
      cardContent: /* @__PURE__ */ jsxs17(FlexCol5, { alignItems: "flex-start", children: [
        /* @__PURE__ */ jsxs17(FlexRow7, { gap: 0.5, paddingBottom: 1, justifyContent: "flex-start", children: [
          showTooltip ? /* @__PURE__ */ jsx19(Tooltip7, { title: value.toLocaleString(), arrow: true, children: /* @__PURE__ */ jsxs17(Typography9, { variant: "h5", fontWeight: "bold", children: [
            formattedValue,
            " ",
            customUnit
          ] }) }) : /* @__PURE__ */ jsxs17(Typography9, { variant: "h4", fontWeight: "bold", children: [
            formattedValue,
            " ",
            customUnit
          ] }),
          /* @__PURE__ */ jsxs17(FlexRow7, { alignItems: "center", children: [
            isPositive ? /* @__PURE__ */ jsx19(ArrowUpwardRounded2, { sx: { color: changeColor, fontSize: theme.typography.body2.fontSize } }) : /* @__PURE__ */ jsx19(ArrowDownwardRounded2, { sx: { color: changeColor, fontSize: theme.typography.body2.fontSize } }),
            /* @__PURE__ */ jsxs17(Typography9, { variant: "body2", sx: { color: changeColor }, children: [
              Math.abs(change),
              "%"
            ] })
          ] })
        ] }),
        isDefined4(description) && /* @__PURE__ */ jsx19(Typography9, { variant: "body2", sx: { color: changeColor }, children: description })
      ] })
    }
  );
};

// src/showcase/dashboard/DataViewers/VerificationSummary/VerificationSummaryProps.tsx
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
var VerificationSummary = ({
  currentLocation,
  regions,
  title,
  finalConfirmation,
  locationHeader,
  timeHeader,
  statusHeader
}) => {
  return /* @__PURE__ */ jsx20(
    DataCard,
    {
      headerTitle: title,
      cardContent: /* @__PURE__ */ jsxs18(Box7, { display: "flex", justifyContent: "space-between", flexDirection: "column", children: [
        /* @__PURE__ */ jsx20(Typography10, { variant: "body2", children: "Current Location:" }),
        /* @__PURE__ */ jsx20(Typography10, { variant: "h6", fontWeight: "bold", sx: { mb: 2 }, children: currentLocation }),
        /* @__PURE__ */ jsx20(
          VerificationList,
          {
            regions,
            locationHeader,
            timeHeader,
            statusHeader
          }
        ),
        finalConfirmation && /* @__PURE__ */ jsx20(Box7, { sx: { textAlign: "center", mt: 2 }, children: /* @__PURE__ */ jsxs18(FlexCol6, { alignItems: "flex-start", paddingTop: 2, children: [
          /* @__PURE__ */ jsx20(Typography10, { variant: "body2", children: "Customer Confirmation:" }),
          /* @__PURE__ */ jsx20(Typography10, { variant: "h6", gutterBottom: true, fontWeight: "bold", children: "Complete \u2705" }),
          /* @__PURE__ */ jsx20(Typography10, { variant: "body2", children: "Your order has reached the final destination." })
        ] }) })
      ] })
    }
  );
};

// src/showcase/dashboard/ExampleDashboards/SampleDashboard1.tsx
import {
  Box as Box8,
  Grid,
  Typography as Typography11
} from "@mui/material";
import { FlexRow as FlexRow8 } from "@xylabs/react-flexbox";
import React10 from "react";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
var SampleDashboard1 = ({
  dataMapping,
  depin,
  employeeTracking,
  eventAttendance,
  gamefi,
  mobileApp,
  partnerValidation,
  rewardPrograms,
  supplyChain
}) => {
  const [category, setCategory] = React10.useState("employeeTracking");
  const dataMappingObject = {
    dataMapping,
    depin,
    employeeTracking,
    eventAttendance,
    gamefi,
    mobileApp,
    partnerValidation,
    rewardPrograms,
    supplyChain
  };
  const currentData = dataMappingObject[category];
  console.log("Selected Category:", category);
  console.log("Current Data:", currentData);
  const handleCategoryChange = (value) => {
    console.log("Dropdown selected value:", value);
    setCategory(value);
  };
  return /* @__PURE__ */ jsxs19(Grid, { container: true, spacing: 2, children: [
    /* @__PURE__ */ jsx21(Grid, { size: { xs: 12 }, children: /* @__PURE__ */ jsxs19(FlexRow8, { justifyContent: "space-between", flexWrap: "wrap", children: [
      /* @__PURE__ */ jsx21(Typography11, { variant: "h3", children: "Dashboard" }),
      /* @__PURE__ */ jsx21(Box8, { width: "fit-content", children: /* @__PURE__ */ jsx21(
        DropdownSelect,
        {
          items: DropdownSelectCategories,
          onChange: handleCategoryChange,
          selectedValue: category
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard1 })
      }
    ),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard2 })
      }
    ),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard3 })
      }
    ),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard4 })
      }
    ),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard5 })
      }
    ),
    /* @__PURE__ */ jsx21(
      Grid,
      {
        size: {
          xs: 12,
          md: 4,
          sm: 6
        },
        children: /* @__PURE__ */ jsx21(StatCard, { ...currentData.statCard6 })
      }
    ),
    /* @__PURE__ */ jsx21(Grid, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx21(VerificationSummary, { ...currentData.verificationSummary }) }),
    /* @__PURE__ */ jsx21(Grid, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx21(DataCard, { ...currentData.dataCard }) })
  ] });
};

// src/showcase/dashboard/ExampleDashboards/SampleDashboard2.tsx
import {
  Box as Box9,
  Grid as Grid2,
  Typography as Typography12
} from "@mui/material";
import { FlexRow as FlexRow9 } from "@xylabs/react-flexbox";
import React11 from "react";
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var SampleDashboard2 = ({
  dataMapping,
  depin,
  employeeTracking,
  eventAttendance,
  gamefi,
  mobileApp,
  partnerValidation,
  rewardPrograms,
  supplyChain
}) => {
  const [category, setCategory] = React11.useState("employeeTracking");
  const dataMappingObject = {
    dataMapping,
    depin,
    employeeTracking,
    eventAttendance,
    gamefi,
    mobileApp,
    partnerValidation,
    rewardPrograms,
    supplyChain
  };
  const currentData = dataMappingObject[category];
  const handleCategoryChange = (value) => {
    console.log("Dropdown selected value:", value);
    setCategory(value);
  };
  return /* @__PURE__ */ jsxs20(Grid2, { container: true, spacing: 2, children: [
    /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12 }, children: /* @__PURE__ */ jsxs20(FlexRow9, { justifyContent: "space-between", flexWrap: "wrap", children: [
      /* @__PURE__ */ jsx22(Typography12, { variant: "h3", children: "Dashboard" }),
      /* @__PURE__ */ jsx22(Box9, { width: "fit-content", children: /* @__PURE__ */ jsx22(
        DropdownSelect,
        {
          items: DropdownSelectCategories,
          onChange: handleCategoryChange,
          selectedValue: category
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsxs20(Grid2, { container: true, spacing: 2, children: [
      /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx22(StatCard, { ...currentData.statCard1 }) }),
      /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx22(StatCard, { ...currentData.statCard2 }) }),
      /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx22(StatCard, { ...currentData.statCard3 }) }),
      /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx22(StatCard, { ...currentData.statCard4 }) })
    ] }) }),
    /* @__PURE__ */ jsx22(Grid2, { size: { xs: 12, md: 6 }, children: /* @__PURE__ */ jsx22(DataCard, { cardContent: /* @__PURE__ */ jsx22(StyledDualRingPieChart, { ...currentData.pieChart }) }) })
  ] });
};

// src/showcase/theme/ColorCard.tsx
import {
  Box as Box10,
  Typography as Typography13
} from "@mui/material";
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var ColorCard = ({
  colorName,
  subtype,
  ...props
}) => {
  return /* @__PURE__ */ jsxs21(
    Box10,
    {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flex: "1 1 0px",
      padding: 2,
      ...props,
      children: [
        /* @__PURE__ */ jsxs21(Box10, { flexDirection: "column", children: [
          /* @__PURE__ */ jsx23(Typography13, { variant: "caption", children: subtype }),
          /* @__PURE__ */ jsx23(Typography13, { variant: "h6", children: colorName })
        ] }),
        /* @__PURE__ */ jsx23(Typography13, { alignSelf: "flex-end", variant: "caption", children: colorName })
      ]
    }
  );
};

// src/showcase/theme/ColorShowcase.tsx
import {
  Box as Box11,
  useTheme as useTheme13
} from "@mui/material";
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var ColorShowcase = () => {
  const theme = useTheme13();
  return /* @__PURE__ */ jsxs22(Box11, { display: "flex", flexDirection: "column", flex: "1 1 0px", children: [
    /* @__PURE__ */ jsx24(ColorSchemeButton, {}),
    /* @__PURE__ */ jsxs22(Box11, { display: "flex", flex: "1 1 0px", flexDirection: "row", justifyContent: "stretch", children: [
      /* @__PURE__ */ jsx24(
        ColorCard,
        {
          style: { background: theme.vars.palette.background.gradient },
          color: theme.vars.palette.background.gradient,
          colorName: "Background",
          subtype: "Gradient"
        }
      ),
      /* @__PURE__ */ jsx24(
        ColorCard,
        {
          color: theme.vars.palette.text.primary,
          bgcolor: theme.vars.palette.background.default,
          colorName: "Background",
          subtype: "Default"
        }
      ),
      /* @__PURE__ */ jsx24(
        ColorCard,
        {
          color: theme.vars.palette.text.primary,
          bgcolor: theme.vars.palette.background.paper,
          colorName: "Background",
          subtype: "Paper"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs22(Box11, { display: "flex", flex: "1 1 0px", flexDirection: "row", justifyContent: "stretch", children: [
      /* @__PURE__ */ jsx24(
        ColorCard,
        {
          color: theme.vars.palette.primary.contrastText,
          bgcolor: theme.vars.palette.primary.main,
          colorName: "Primary"
        }
      ),
      /* @__PURE__ */ jsx24(
        ColorCard,
        {
          color: theme.vars.palette.secondary.contrastText,
          bgcolor: theme.vars.palette.secondary.main,
          colorName: "Secondary"
        }
      ),
      /* @__PURE__ */ jsxs22(Box11, { display: "flex", flex: "1 1 0px", flexDirection: "column", children: [
        /* @__PURE__ */ jsxs22(Box11, { display: "flex", flex: "1 1 0px", flexDirection: "row", children: [
          /* @__PURE__ */ jsx24(
            ColorCard,
            {
              color: theme.vars.palette.info.contrastText,
              bgcolor: theme.vars.palette.info.main,
              colorName: "Info"
            }
          ),
          /* @__PURE__ */ jsx24(
            ColorCard,
            {
              color: theme.vars.palette.success.contrastText,
              bgcolor: theme.vars.palette.success.main,
              colorName: "Success"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs22(Box11, { display: "flex", flex: "1 1 0px", flexDirection: "row", children: [
          /* @__PURE__ */ jsx24(
            ColorCard,
            {
              color: theme.vars.palette.warning.contrastText,
              bgcolor: theme.vars.palette.warning.main,
              colorName: "Warning"
            }
          ),
          /* @__PURE__ */ jsx24(
            ColorCard,
            {
              color: theme.vars.palette.error.contrastText,
              bgcolor: theme.vars.palette.error.main,
              colorName: "Error"
            }
          )
        ] })
      ] })
    ] })
  ] });
};

// src/showcase/theme/ThemeShowcase.tsx
import { Info } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar as AppBar2,
  Button as Button2,
  Card as Card4,
  CardContent as CardContent3,
  CardHeader as CardHeader2,
  Chip,
  Dialog as Dialog2,
  DialogActions as DialogActions2,
  DialogContent as DialogContent2,
  DialogContentText as DialogContentText2,
  DialogTitle as DialogTitle2,
  Link,
  Paper,
  Stack as Stack2,
  Tooltip as Tooltip8,
  Typography as Typography14
} from "@mui/material";
import { useState as useState9 } from "react";
import { Fragment as Fragment3, jsx as jsx25, jsxs as jsxs23 } from "react/jsx-runtime";
var ThemeShowcase = () => {
  const [open, setOpen] = useState9(false);
  return /* @__PURE__ */ jsxs23(Fragment3, { children: [
    /* @__PURE__ */ jsx25(AppBar2, { position: "sticky", sx: { mb: 2 }, children: /* @__PURE__ */ jsx25(ColorSchemeButton, {}) }),
    /* @__PURE__ */ jsxs23(Stack2, { gap: 2, children: [
      /* @__PURE__ */ jsx25(Typography14, { variant: "h4", gutterBottom: true, children: "Theme Showcase" }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "primary", variant: "contained", children: "Primary" }),
        /* @__PURE__ */ jsx25(Button2, { color: "primary", variant: "outlined", children: "Primary" }),
        /* @__PURE__ */ jsx25(Button2, { color: "primary", variant: "text", children: "Primary" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "secondary", variant: "contained", children: "Secondary" }),
        /* @__PURE__ */ jsx25(Button2, { color: "secondary", variant: "outlined", children: "Secondary" }),
        /* @__PURE__ */ jsx25(Button2, { color: "secondary", variant: "text", children: "Secondary" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "success", variant: "contained", children: "Success" }),
        /* @__PURE__ */ jsx25(Button2, { color: "success", variant: "outlined", children: "Success" }),
        /* @__PURE__ */ jsx25(Button2, { color: "success", variant: "text", children: "Success" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "info", variant: "contained", children: "Info" }),
        /* @__PURE__ */ jsx25(Button2, { color: "info", variant: "outlined", children: "Info" }),
        /* @__PURE__ */ jsx25(Button2, { color: "info", variant: "text", children: "Info" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "warning", variant: "contained", children: "Warning" }),
        /* @__PURE__ */ jsx25(Button2, { color: "warning", variant: "outlined", children: "Warning" }),
        /* @__PURE__ */ jsx25(Button2, { color: "warning", variant: "text", children: "Warning" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "error", variant: "contained", children: "Error" }),
        /* @__PURE__ */ jsx25(Button2, { color: "error", variant: "outlined", children: "Error" }),
        /* @__PURE__ */ jsx25(Button2, { color: "error", variant: "text", children: "Error" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Button2, { color: "neutral", variant: "contained", children: "Neutral" }),
        /* @__PURE__ */ jsx25(Button2, { color: "neutral", variant: "outlined", children: "Neutral" }),
        /* @__PURE__ */ jsx25(Button2, { color: "neutral", variant: "text", children: "Neutral" })
      ] }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsx25(Link, { href: top?.location.href, children: "Default" }),
        /* @__PURE__ */ jsx25(Link, { color: "neutral", href: top?.location.href, children: "Neutral" }),
        /* @__PURE__ */ jsx25(Link, { color: "primary", href: top?.location.href, children: "Primary" }),
        /* @__PURE__ */ jsx25(Link, { color: "secondary", href: top?.location.href, children: "Secondary" })
      ] }),
      /* @__PURE__ */ jsx25(Stack2, { flexDirection: "row", gap: 2, children: /* @__PURE__ */ jsx25(Paper, { sx: { padding: 2 }, children: "Paper with default elevation" }) }),
      /* @__PURE__ */ jsx25(Stack2, { flexDirection: "row", gap: 2, children: /* @__PURE__ */ jsx25(
        Tooltip8,
        {
          title: "A really long tooltip that should wrap to a new line.",
          placement: "top",
          children: /* @__PURE__ */ jsx25(Chip, { avatar: /* @__PURE__ */ jsx25(Info, {}), label: "Tooltip Demo" })
        }
      ) }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "row", gap: 2, children: [
        /* @__PURE__ */ jsxs23(Dialog2, { open, onClose: () => setOpen(false), children: [
          /* @__PURE__ */ jsx25(DialogTitle2, { children: "Dialog" }),
          /* @__PURE__ */ jsx25(DialogContent2, { children: /* @__PURE__ */ jsx25(DialogContentText2, { children: "This is a dialog. It can be used to display information or prompt the user for input." }) }),
          /* @__PURE__ */ jsxs23(DialogActions2, { children: [
            /* @__PURE__ */ jsx25(Button2, { variant: "outlined", onClick: () => setOpen(false), children: "Cancel" }),
            /* @__PURE__ */ jsx25(Button2, { variant: "contained", onClick: () => setOpen(false), autoFocus: true, children: "Ok" })
          ] })
        ] }),
        /* @__PURE__ */ jsx25(Button2, { variant: "outlined", onClick: () => setOpen(true), children: "Open Dialog" })
      ] }),
      /* @__PURE__ */ jsx25(Stack2, { flexDirection: "row", gap: 2, children: /* @__PURE__ */ jsxs23(Card4, { children: [
        /* @__PURE__ */ jsx25(CardHeader2, { title: "Card Header", subheader: "Card Subheader" }),
        /* @__PURE__ */ jsx25(CardContent3, { children: /* @__PURE__ */ jsx25(Typography14, { variant: "body1", children: "This is a card. It can be used to display information or prompt the user for input." }) })
      ] }) }),
      /* @__PURE__ */ jsxs23(Stack2, { flexDirection: "column", children: [
        /* @__PURE__ */ jsxs23(Accordion, { children: [
          /* @__PURE__ */ jsx25(AccordionSummary, { children: "Accordion Summary 1" }),
          /* @__PURE__ */ jsx25(AccordionDetails, { children: "Accordion Details" })
        ] }),
        /* @__PURE__ */ jsxs23(Accordion, { children: [
          /* @__PURE__ */ jsx25(AccordionSummary, { children: "Accordion Summary 2" }),
          /* @__PURE__ */ jsx25(AccordionDetails, { children: "Accordion Details" })
        ] })
      ] })
    ] })
  ] });
};

// src/useIsDark.ts
import { useColorScheme as useColorScheme2 } from "@mui/material";
var useIsDark = () => {
  const colorScheme = useColorScheme2();
  return colorScheme.mode === "system" ? colorScheme.systemMode === "dark" : colorScheme.mode === "dark";
};

// src/useIsSmall.ts
import { useMediaQuery, useTheme as useTheme14 } from "@mui/material";
var useIsSmall = () => {
  const theme = useTheme14();
  return useMediaQuery(theme.breakpoints.down("md"));
};
export {
  AnimatedIcon,
  AppChrome,
  BaseMenuList,
  ColorCard,
  ColorSchemeButton,
  ColorShowcase,
  CustomDataTooltip,
  DailyVerificationLineChart,
  DailyVerificationScatterChart,
  DailyVerificationStackedAreaChart,
  DataCard,
  DataCardSpeedDial,
  DataHealth,
  DataismTheme,
  DataismThemeOptions,
  DropdownSelect,
  DropdownSelectCategories,
  sampleVerificationData_exports as LineChartSampleData,
  LogoutListItem,
  MiniDrawer,
  SampleDashboard1,
  SampleDashboard2,
  sampleVerificationData_exports2 as ScatterChartSampleData,
  StatCard,
  StyledDualRingPieChart,
  ThemeShowcase,
  VerificationList,
  VerificationSummary,
  Xl1Theme,
  Xl1ThemeOptions,
  XyLabsTheme,
  XyLabsThemeOptions,
  XyOSThemeOptions,
  XyoTheme,
  XyoThemeBreakpoints,
  XyoThemeComponents,
  XyoThemeDarkThemeOptions,
  XyoThemeLightThemeOptions,
  XyoThemeOptions,
  XyoThemeTypography,
  XyosTheme,
  alphaCss,
  darkenCss,
  display1,
  display2,
  generateVerificationData,
  lightenCss,
  stylize1,
  stylize2,
  typographyFragmentDisplay1,
  typographyFragmentDisplay2,
  typographyFragmentStylize1,
  typographyFragmentStylize2,
  useIsDark,
  useIsSmall
};
//# sourceMappingURL=index.mjs.map